import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { servicioActualizacionDiaria } from "./servicios/actualizacionDiaria";
import {
  Calendar,
  Plus,
  Save,
  X,
  Edit,
  Trash2,
  Clock,
  CheckCircle2,
  Building2,
  ChevronDown,
  Search,
  Play,
  Pause,
  BarChart3,
  Loader2,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  Link,
  Eye,
  Target,
  ArrowRight,
  Info,
  ChevronLeft,
  ChevronRight,
  Star,
  Crown,
  CalendarCheck,
  Zap,
  TrendingDown,
  AlertCircle,
  Activity,
  Calculator,
  User,
  HardHat,
  Settings,
  Percent,
  RotateCcw,
  FileText,
  Filter,
} from "lucide-react";
import { obtenerPermisosCliente, puedeAccederAVista } from "./permisos";

// Configuración anti-zoom obligatoria
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// Colores cronograma según sistema de diseño
const getVistaColor = () => ({
  gradient: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
  solid: "#2563eb",
  light: "#2563eb10",
  description: "Cronograma de proyecto",
});

// ✅ FUNCIONES REUTILIZADAS DE PRESUPUESTOSINICIALES
const formatearFechaSinTimezone = (fecha) => {
  if (!fecha) return null;
  const fechaObj = new Date(fecha);
  return `${fechaObj.getFullYear()}-${String(fechaObj.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(fechaObj.getDate()).padStart(2, "0")}`;
};

const convertirMonto = (
  monto,
  monedaOrigen,
  monedaDestino,
  cotizacionDolar
) => {
  if (!monto || monedaOrigen === monedaDestino) return monto;

  if (monedaOrigen === "USD" && monedaDestino === "ARS") {
    return monto * cotizacionDolar;
  } else if (monedaOrigen === "ARS" && monedaDestino === "USD") {
    return monto / cotizacionDolar;
  }

  return monto;
};

// ✅ NUEVA LÓGICA: Cargar presupuestos desde tabla unificada
const cargarPresupuestosActualizados = async (
  proyectoId,
  etapaSeleccionada = null,
  subetapaSeleccionada = null
) => {
  if (!proyectoId) return [];

  try {
    console.log("🔄 Cargando presupuestos actualizados:", {
      proyectoId,
      etapaSeleccionada,
      subetapaSeleccionada,
    });

    let query = supabase
      .from("presupuestos_iniciales_actualizados")
      .select("*")
      .eq("proyecto_id", proyectoId)
      .order("tipo", { ascending: true })
      .order("nombre_presupuesto", { ascending: true });

    const { data: todosPresupuestos, error } = await query;

    if (error) {
      console.error("❌ Error cargando presupuestos:", error);
      return [];
    }

    if (!todosPresupuestos || todosPresupuestos.length === 0) {
      console.warn("⚠️ No se encontraron presupuestos actualizados");
      return [];
    }

    // Filtrar presupuestos según reglas de negocio
    const presupuestosFiltrados = todosPresupuestos.filter((presupuesto) => {
      // SIEMPRE mostrar honorarios y mano_obra
      if (
        presupuesto.tipo === "honorarios" ||
        presupuesto.tipo === "mano_obra"
      ) {
        return true;
      }

      // Para presupuestos de obra, aplicar filtros (permitiendo mostrar sin filtros)
      if (presupuesto.tipo === "obra") {
        if (etapaSeleccionada && presupuesto.etapa_id !== etapaSeleccionada) {
          return false;
        }
        if (
          subetapaSeleccionada &&
          presupuesto.subetapa_id !== subetapaSeleccionada
        ) {
          return false;
        }
        return true;
      }

      return false;
    });

    console.log("✅ Presupuestos filtrados:", {
      total: todosPresupuestos.length,
      filtrados: presupuestosFiltrados.length,
      tipos: presupuestosFiltrados.reduce((acc, p) => {
        acc[p.tipo] = (acc[p.tipo] || 0) + 1;
        return acc;
      }, {}),
    });

    return presupuestosFiltrados;
  } catch (err) {
    console.error("💥 Error en cargarPresupuestosActualizados:", err);
    return [];
  }
};

// ✅ NUEVA LÓGICA: Cálculo de distribución automática
const calcularDistribucionAutomatica = (presupuestos, tareas) => {
  const distribucion = {};

  // Agrupar tareas por presupuesto
  const tareasPorPresupuesto = {};

  tareas.forEach((tarea) => {
    const vinculados = Array.isArray(tarea.presupuestos_vinculados)
      ? tarea.presupuestos_vinculados
      : (typeof tarea.presupuestos_vinculados === "string"
          ? (() => { try { const arr = JSON.parse(tarea.presupuestos_vinculados); return Array.isArray(arr) ? arr : []; } catch { return []; } })()
          : []);
    if (vinculados.length > 0) {
      vinculados.forEach((presupuestoId) => {
        if (!tareasPorPresupuesto[presupuestoId]) {
          tareasPorPresupuesto[presupuestoId] = [];
        }
        tareasPorPresupuesto[presupuestoId].push(tarea);
      });
    }
  });

  // Calcular distribución por cada presupuesto
  Object.keys(tareasPorPresupuesto).forEach((presupuestoId) => {
    const presupuesto = presupuestos.find((p) => p.id === presupuestoId);
    if (!presupuesto) return;

    const tareasVinculadas = tareasPorPresupuesto[presupuestoId];
    const totalDias = tareasVinculadas.reduce((sum, tarea) => {
      const dias =
        Math.ceil(
          (new Date(tarea.fecha_fin + "T00:00:00") - new Date(tarea.fecha_inicio + "T00:00:00")) /
            (1000 * 60 * 60 * 24)
        ) + 1;
      return sum + dias;
    }, 0);

    tareasVinculadas.forEach((tarea) => {
      const dias =
        Math.ceil(
          (new Date(tarea.fecha_fin + "T00:00:00") - new Date(tarea.fecha_inicio + "T00:00:00")) /
            (1000 * 60 * 60 * 24)
        ) + 1;

      const proporcion = totalDias > 0 ? dias / totalDias : 0;
      const costoAsignado = presupuesto.total_actualizado_ars * proporcion;

      if (!distribucion[tarea.id]) {
        distribucion[tarea.id] = {
          tareaId: tarea.id,
          costoAsignadoTotal: 0,
          costoGastadoTotal: 0,
          presupuestosVinculados: [],
          avanceFinanciero: 0,
          alertaFinanciera: false,
        };
      }

      distribucion[tarea.id].costoAsignadoTotal += costoAsignado;
      distribucion[tarea.id].presupuestosVinculados.push({
        presupuestoId,
        nombre: presupuesto.nombre_presupuesto,
        tipo: presupuesto.tipo,
        costoAsignado,
        proporcion,
        gastado: 0,
      });
    });

    // ✅ Asignar pagos usando lógica FIFO (tareas más antiguas primero)
    let pagosRestantes = Number(presupuesto.pagado_ars) || 0;
    if (pagosRestantes > 0) {
      const tareasOrdenadas = [...tareasVinculadas].sort((a, b) => {
        const ai = new Date((a.fecha_inicio || "") + "T00:00:00");
        const bi = new Date((b.fecha_inicio || "") + "T00:00:00");
        if (ai.getTime() !== bi.getTime()) return ai - bi;
        const af = new Date((a.fecha_fin || "") + "T00:00:00");
        const bf = new Date((b.fecha_fin || "") + "T00:00:00");
        return af - bf;
      });

      for (const tarea of tareasOrdenadas) {
        const infoTarea = distribucion[tarea.id];
        if (!infoTarea) continue;
        const vinc = infoTarea.presupuestosVinculados.find(
          (v) => v.presupuestoId === presupuestoId
        );
        if (!vinc) continue;
        const asignado = Number(vinc.costoAsignado) || 0;
        if (asignado <= 0) continue;
        const puedeImputar = Math.min(asignado - (Number(vinc.gastado) || 0), pagosRestantes);
        if (puedeImputar > 0) {
          vinc.gastado = (Number(vinc.gastado) || 0) + puedeImputar;
          infoTarea.costoGastadoTotal += puedeImputar;
          pagosRestantes -= puedeImputar;
        }
        if (pagosRestantes <= 0) break;
      }
    }
  });

  // Calcular % avance y alertas con los montos ya asignados por FIFO
  Object.values(distribucion).forEach((info) => {
    info.avanceFinanciero =
      info.costoAsignadoTotal > 0
        ? (info.costoGastadoTotal / info.costoAsignadoTotal) * 100
        : 0;
    
    // ✅ PREVENIR NaN
    if (isNaN(info.avanceFinanciero)) info.avanceFinanciero = 0;
    if (isNaN(info.costoAsignadoTotal)) info.costoAsignadoTotal = 0;
    if (isNaN(info.costoGastadoTotal)) info.costoGastadoTotal = 0;

    // Buscar la tarea para comparar con progreso físico
    const tarea = tareas.find((t) => t.id === info.tareaId);
    if (tarea) {
      info.alertaFinanciera =
        info.avanceFinanciero > (tarea.progreso_porcentaje || 0) + 10;
    }
  });

  return distribucion;
};

// Dropdown personalizado unificado con búsqueda
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "12px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
        onFocus={(e) => {
          if (!disabled) {
            e.target.style.borderColor = "#7c3aed";
            e.target.style.boxShadow = "0 0 0 3px rgba(124, 58, 237, 0.1)";
          }
        }}
        onBlur={(e) => {
          if (!disabled) {
            e.target.style.borderColor = "#f1f5f9";
            e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
          }
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "280px",
            overflowY: "auto",
          }}
        >
          {/* Campo de búsqueda */}
          <div style={{ padding: "8px" }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
            />
          </div>

          {/* Opciones filtradas */}
          {filteredOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
                setSearchTerm("");
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#7c3aed" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "#f8fafc";
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "transparent";
                }
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {option.icon && option.icon}
                <span>{option.label}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ NUEVO DROPDOWN DE PRESUPUESTOS MÚLTIPLES CON BÚSQUEDA
const PresupuestosMultiplesDropdown = ({
  presupuestosSeleccionados = [],
  onPresupuestosChange,
  presupuestosDisponibles = [],
  disabled = false,
  placeholder = "Seleccionar presupuestos",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const presupuestosFiltrados = presupuestosDisponibles.filter(
    (presupuesto) =>
      (presupuesto.nombre_presupuesto || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      presupuesto.tipo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleTogglePresupuesto = (presupuestoId) => {
    const yaSeleccionado = presupuestosSeleccionados.includes(presupuestoId);

    if (yaSeleccionado) {
      onPresupuestosChange(
        presupuestosSeleccionados.filter((id) => id !== presupuestoId)
      );
    } else {
      onPresupuestosChange([...presupuestosSeleccionados, presupuestoId]);
    }
  };

  const getDisplayText = () => {
    if (presupuestosSeleccionados.length === 0) {
      return placeholder;
    }

    if (presupuestosSeleccionados.length === 1) {
      const presupuesto = presupuestosDisponibles.find(
        (p) => p.id === presupuestosSeleccionados[0]
      );
      return presupuesto ? (presupuesto.nombre_presupuesto || `${presupuesto.tipo} - ${presupuesto.id.slice(0, 8)}`) : "1 presupuesto";
    }

    return `${presupuestosSeleccionados.length} presupuestos seleccionados`;
  };

  const getTotalSeleccionado = () => {
    return presupuestosSeleccionados.reduce((total, id) => {
      const presupuesto = presupuestosDisponibles.find((p) => p.id === id);
      return total + (presupuesto ? (presupuesto.total_actualizado_ars || 0) : 0);
    }, 0);
  };

  const getIconoTipo = (tipo) => {
    switch (tipo) {
      case "honorarios":
        return <User size={14} color="#3b82f6" />;
      case "mano_obra":
        return <HardHat size={14} color="#10b981" />;
      case "obra":
        return <Building2 size={14} color="#f59e0b" />;
      default:
        return <Calculator size={14} color="#64748b" />;
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "12px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
        onFocus={(e) => {
          if (!disabled) {
            e.target.style.borderColor = "#7c3aed";
            e.target.style.boxShadow = "0 0 0 3px rgba(124, 58, 237, 0.1)";
          }
        }}
        onBlur={(e) => {
          if (!disabled) {
            e.target.style.borderColor = "#f1f5f9";
            e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
          }
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          <Calculator size={16} color="#64748b" />
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getDisplayText()}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {/* Resumen de selección */}
      {presupuestosSeleccionados.length > 0 && (
        <div
          style={{
            marginTop: "8px",
            padding: "8px 12px",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "8px",
            fontSize: "12px",
            color: "#166534",
            fontWeight: "600",
          }}
        >
          Total asignado: ${getTotalSeleccionado().toLocaleString("es-AR")} ARS
        </div>
      )}

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          {/* Campo de búsqueda */}
          <div style={{ padding: "12px", borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ position: "relative" }}>
              <Search
                size={16}
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#64748b",
                }}
              />
              <input
                type="text"
                placeholder="Buscar presupuestos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px 8px 40px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          {/* Opción para limpiar selección */}
          <button
            onClick={() => onPresupuestosChange([])}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "none",
              background: "#fef2f2",
              color: "#ef4444",
              fontSize: "14px",
              fontWeight: "600",
              textAlign: "left",
              cursor: "pointer",
              transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <X size={14} />
            Sin vincular a presupuestos
          </button>

          {/* Lista de presupuestos */}
          <div style={{ padding: "8px 0" }}>
            {presupuestosFiltrados.length === 0 ? (
              <div
                style={{
                  padding: "20px",
                  textAlign: "center",
                  color: "#64748b",
                  fontSize: "14px",
                }}
              >
                No se encontraron presupuestos
              </div>
            ) : (
              presupuestosFiltrados.map((presupuesto) => {
                const estaSeleccionado = presupuestosSeleccionados.includes(
                  presupuesto.id
                );

                return (
                  <label
                    key={presupuesto.id}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      background: estaSeleccionado ? "#eff6ff" : "transparent",
                      color: estaSeleccionado ? "#7c3aed" : "#374151",
                      fontSize: "14px",
                      fontWeight: estaSeleccionado ? "600" : "400",
                      textAlign: "left",
                      cursor: "pointer",
                      transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                      touchAction: "manipulation",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                      border: "none",
                      userSelect: "none",
                    }}
                    onMouseEnter={(e) => {
                      if (!estaSeleccionado) {
                        e.target.style.background = "#f8fafc";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!estaSeleccionado) {
                        e.target.style.background = "transparent";
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={estaSeleccionado}
                      onChange={() => handleTogglePresupuesto(presupuesto.id)}
                      style={{
                        margin: "2px 0 0 0",
                        accentColor: "#7c3aed",
                      }}
                    />

                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "4px",
                        }}
                      >
                        {getIconoTipo(presupuesto.tipo)}
                        <span style={{ fontWeight: "600" }}>
                          {presupuesto.nombre_presupuesto}
                        </span>
                        <span
                          style={{
                            background:
                              presupuesto.tipo === "honorarios"
                                ? "#dbeafe"
                                : presupuesto.tipo === "mano_obra"
                                ? "#dcfce7"
                                : "#fef3c7",
                            color:
                              presupuesto.tipo === "honorarios"
                                ? "#1e40af"
                                : presupuesto.tipo === "mano_obra"
                                ? "#166534"
                                : "#92400e",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "10px",
                            fontWeight: "600",
                            textTransform: "uppercase",
                          }}
                        >
                          {presupuesto.tipo}
                        </span>
                      </div>

                      <div
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          $
                          {(presupuesto.total_actualizado_ars || 0).toLocaleString(
                            "es-AR"
                          )}{" "}
                          ARS
                        </span>
                        <span>
                          {presupuesto.pagado_ars && presupuesto.total_actualizado_ars 
                            ? ((presupuesto.pagado_ars / presupuesto.total_actualizado_ars) * 100).toFixed(1)
                            : 0
                          }% pagado
                        </span>
                      </div>

                      {presupuesto.descripcion && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#9ca3af",
                            marginTop: "2px",
                            fontStyle: "italic",
                          }}
                        >
                          {presupuesto.descripcion}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Modal de alertas personalizado
const CustomModal = ({
  isOpen,
  onClose,
  type = "info",
  title,
  message,
  onConfirm,
  confirmText = "Aceptar",
  cancelText = "Cancelar",
  isMobile,
  options = null,
}) => {
  // Prevenir scroll del body cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getColors = () => {
    switch (type) {
      case "confirm":
        return {
          bg: "#fef3cd",
          border: "#f59e0b",
          text: "#92400e",
          button: "#f59e0b",
          icon: AlertTriangle,
        };
      case "error":
        return {
          bg: "#fef2f2",
          border: "#ef4444",
          text: "#dc2626",
          button: "#ef4444",
          icon: X,
        };
      case "success":
        return {
          bg: "#f0fdf4",
          border: "#22c55e",
          text: "#166534",
          button: "#22c55e",
          icon: CheckCircle2,
        };
      default:
        return {
          bg: "#f0f9ff",
          border: "#3b82f6",
          text: "#1d4ed8",
          button: "#3b82f6",
          icon: Info,
        };
    }
  };

  const colors = getColors();
  const IconComponent = colors.icon;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
        padding: "20px",
        overflow: "hidden", // Prevenir scroll del modal container
      }}
      onClick={(e) => {
        // Cerrar modal al hacer click en el backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          padding: "24px",
          width: "100%",
          maxWidth: isMobile ? "90%" : "480px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: `2px solid ${colors.border}`,
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "24px",
                background: colors.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconComponent size={24} color={colors.text} />
            </div>
          </div>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: colors.text,
            }}
          >
            {title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "#64748b",
              lineHeight: "1.4",
            }}
          >
            {message}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile || !options ? '1fr' : `repeat(${1 + options.length}, 1fr)`,
            gap: "12px",
            alignItems: "stretch",
          }}
        >
          {options ? (
            <>
              <button
                onClick={onClose}
                style={{
                  padding: "12px 16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "12px",
                  background: "white",
                  color: "#64748b",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                {cancelText}
              </button>
              {options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    try { if (typeof opt.onClick === 'function') opt.onClick(); } finally { onClose(); }
                  }}
                  style={{
                    padding: "12px 16px",
                    border: "none",
                    borderRadius: "12px",
                    background: colors.button,
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </>
          ) : (
            <>
            {type === "confirm" && (
              <button
                onClick={onClose}
                style={{
                  padding: "12px 16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "12px",
                  background: "white",
                  color: "#64748b",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                {cancelText}
              </button>
            )}
            <button
              onClick={() => {
                if (onConfirm) onConfirm();
                onClose();
              }}
              style={{
                padding: "12px 20px",
                border: "none",
                borderRadius: "12px",
                background: colors.button,
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              {confirmText}
            </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Tooltip personalizado con información financiera
const TaskTooltip = ({
  tarea,
  etapas,
  subetapas,
  position,
  visible,
  infoFinanciera,
}) => {
  if (!visible || !tarea) return null;

  const etapa = etapas.find((e) => e.id === tarea.etapa_id);
  const subetapa = subetapas.find((s) => s.id === tarea.subetapa_id);
  const info = infoFinanciera[tarea.id];

  const diasDuracion =
    Math.ceil(
      (new Date(tarea.fecha_fin + "T00:00:00") - new Date(tarea.fecha_inicio + "T00:00:00")) /
        (1000 * 60 * 60 * 24)
    ) + 1;

  const isOverdue = (() => {
    // ✅ CORREGIR DESFASE: Usar fecha actual sin timezone
    const today = new Date();
    const todaySinTZ = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const fechaFin = new Date(tarea.fecha_fin + "T00:00:00");
    return fechaFin < todaySinTZ && tarea.progreso_porcentaje < 100;
  })();

  return (
    <div
      style={{
        position: "fixed",
        left: position.x,
        top: position.y,
        zIndex: 2000,
        background: "rgba(0, 0, 0, 0.9)",
        color: "white",
        padding: "16px 20px",
        borderRadius: "12px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        maxWidth: "380px",
        pointerEvents: "none",
        fontSize: "13px",
        lineHeight: "1.4",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255,255,255,0.1)",
        animation: "fadeIn 0.2s ease",
      }}
    >
      <div
        style={{
          fontWeight: "700",
          fontSize: "14px",
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        {tarea.es_hito && <Crown size={12} color="#f59e0b" />}
        {tarea.nombre}
        {tarea.es_hito && (
          <span
            style={{
              background: "#f59e0b",
              color: "white",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "10px",
              fontWeight: "600",
            }}
          >
            HITO
          </span>
        )}
      </div>

      {tarea.descripcion && (
        <div style={{ marginBottom: "12px", opacity: 0.9 }}>
          {tarea.descripcion}
        </div>
      )}

      <div style={{ display: "grid", gap: "6px", fontSize: "12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ opacity: 0.8 }}>Progreso físico:</span>
          <span
            style={{
              fontWeight: "600",
              color:
                tarea.progreso_porcentaje >= 100
                  ? "#10b981"
                  : tarea.progreso_porcentaje >= 75
                  ? "#f59e0b"
                  : tarea.progreso_porcentaje >= 50
                  ? "#3b82f6"
                  : tarea.progreso_porcentaje > 0
                  ? "#8b5cf6"
                  : "#ef4444",
            }}
          >
            {tarea.progreso_porcentaje}%
          </span>
        </div>

        {/* Información financiera actualizada */}
        {info && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ opacity: 0.8 }}>Progreso financiero:</span>
              <span
                style={{
                  fontWeight: "600",
                  color: info.alertaFinanciera ? "#ef4444" : "#10b981",
                }}
              >
                {isNaN(info.avanceFinanciero) ? "0.0" : info.avanceFinanciero.toFixed(1)}%
              </span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ opacity: 0.8 }}>Costo asignado:</span>
              <span style={{ fontWeight: "600", color: "#f59e0b" }}>
                ${isNaN(info.costoAsignadoTotal) ? "0" : info.costoAsignadoTotal.toLocaleString("es-AR")}
              </span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ opacity: 0.8 }}>Ya gastado:</span>
              <span style={{ fontWeight: "600", color: "#ef4444" }}>
                ${isNaN(info.costoGastadoTotal) ? "0" : info.costoGastadoTotal.toLocaleString("es-AR")}
              </span>
            </div>

            {info.presupuestosVinculados &&
              info.presupuestosVinculados.length > 1 && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "6px 10px",
                    background: "#8b5cf6",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: "600",
                    textAlign: "center",
                  }}
                >
                  {info.presupuestosVinculados.length} presupuestos vinculados
                </div>
              )}

            {info.alertaFinanciera && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "6px 10px",
                  background: "#ef4444",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontWeight: "600",
                  textAlign: "center",
                }}
              >
                ⚠️ Pagando por encima del avance
              </div>
            )}
          </>
        )}

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ opacity: 0.8 }}>Duración:</span>
          <span style={{ fontWeight: "600" }}>{diasDuracion} días</span>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ opacity: 0.8 }}>Inicio:</span>
          <span style={{ fontWeight: "600" }}>
            {(() => {
              const fecha = new Date(tarea.fecha_inicio + "T00:00:00");
              const offset = fecha.getTimezoneOffset() * 60000;
              const fechaAjustada = new Date(fecha.getTime() + offset);
              return fechaAjustada.toLocaleDateString("es-ES");
            })()}
          </span>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ opacity: 0.8 }}>Fin:</span>
          <span
            style={{
              fontWeight: "600",
              color: isOverdue ? "#ef4444" : "white",
            }}
          >
            {(() => {
              const fecha = new Date(tarea.fecha_fin + "T00:00:00");
              const offset = fecha.getTimezoneOffset() * 60000;
              const fechaAjustada = new Date(fecha.getTime() + offset);
              return fechaAjustada.toLocaleDateString("es-ES");
            })()}
          </span>
        </div>

        {etapa && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ opacity: 0.8 }}>Etapa:</span>
            <span style={{ fontWeight: "600" }}>{etapa.nombre}</span>
          </div>
        )}

        {subetapa && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ opacity: 0.8 }}>Subetapa:</span>
            <span style={{ fontWeight: "600" }}>{subetapa.nombre}</span>
          </div>
        )}

        {isOverdue && (
          <div
            style={{
              marginTop: "8px",
              padding: "4px 8px",
              background: "#ef4444",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: "600",
              textAlign: "center",
            }}
          >
            Tarea atrasada
          </div>
        )}
      </div>
    </div>
  );
};

// Gantt Chart con scroll sincronizado
const UnifiedScrollGanttChart = ({
  tareas,
  onTaskClick,
  readOnly = false,
  timeView = "days",
  showCurrency = "ARS",
  tipoCambio = 1000,
  onDeleteTask,
  onRestoreTask,
  filterVigencia,
  etapas = [],
  subetapas = [],
  infoFinanciera = {},
}) => {
  const [tooltip, setTooltip] = useState({
    visible: false,
    tarea: null,
    position: { x: 0, y: 0 },
  });

  // Calcular rango de fechas
  const fechas = tareas.flatMap((t) => [
    new Date(t.fecha_inicio + "T00:00:00"),
    new Date(t.fecha_fin + "T00:00:00"),
  ]);
  const fechaMinima =
    fechas.length > 0 ? new Date(Math.min(...fechas)) : new Date();
  const fechaMaxima =
    fechas.length > 0 ? new Date(Math.max(...fechas)) : new Date();

  const padding = timeView === "days" ? 7 : timeView === "weeks" ? 14 : 30;
  fechaMinima.setDate(fechaMinima.getDate() - padding);
  fechaMaxima.setDate(fechaMaxima.getDate() + padding);

  const getTimeUnits = () => {
    const diffTime = Math.abs(fechaMaxima - fechaMinima);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    switch (timeView) {
      case "weeks":
        return Math.ceil(diffDays / 7);
      case "months":
        return Math.ceil(diffDays / 30);
      default:
        return diffDays;
    }
  };

  const totalUnits = getTimeUnits();
  const unitWidth = timeView === "days" ? 40 : timeView === "weeks" ? 80 : 100;
  const totalWidth = totalUnits * unitWidth;

  const formatTimeUnit = (index) => {
    const date = new Date(fechaMinima);

    switch (timeView) {
      case "weeks":
        date.setDate(fechaMinima.getDate() + index * 7);
        return `S${Math.ceil(index + 1)}`;
      case "months":
        date.setMonth(fechaMinima.getMonth() + index);
        return date.toLocaleDateString("es-ES", { month: "short" });
      default:
        date.setDate(fechaMinima.getDate() + index);
        return date.toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "short",
        });
    }
  };

  const getTaskPosition = (tarea) => {
    // ✅ CORREGIR DESFASE DE FECHAS: Ajustar por timezone
    const inicioDate = new Date(tarea.fecha_inicio + "T00:00:00");
    const finDate = new Date(tarea.fecha_fin + "T00:00:00");
    
    // Ajustar por offset de timezone si es necesario
    const offset = inicioDate.getTimezoneOffset() * 60000;
    const inicioDateAjustado = new Date(inicioDate.getTime() + offset);
    const finDateAjustado = new Date(finDate.getTime() + offset);

    let unitsFromStart, duration;

    switch (timeView) {
      case "weeks":
        unitsFromStart = Math.floor(
          (inicioDateAjustado - fechaMinima) / (1000 * 60 * 60 * 24 * 7)
        );
        duration =
          Math.ceil((finDateAjustado - inicioDateAjustado) / (1000 * 60 * 60 * 24 * 7)) || 1;
        break;
      case "months":
        unitsFromStart = Math.floor(
          (inicioDateAjustado - fechaMinima) / (1000 * 60 * 60 * 24 * 30)
        );
        duration =
          Math.ceil((finDateAjustado - inicioDateAjustado) / (1000 * 60 * 60 * 24 * 30)) || 1;
        break;
      default:
        unitsFromStart = Math.floor(
          (inicioDateAjustado - fechaMinima) / (1000 * 60 * 60 * 24)
        );
        duration =
          Math.ceil((finDateAjustado - inicioDateAjustado) / (1000 * 60 * 60 * 24)) + 1;
    }

    return {
      left: unitsFromStart * unitWidth,
      width: duration * unitWidth,
      duration,
    };
  };

  const getProgressColor = (progreso) => {
    if (progreso >= 100) return "#10b981";
    if (progreso >= 75) return "#f59e0b";
    if (progreso >= 50) return "#3b82f6";
    if (progreso > 0) return "#8b5cf6";
    return "#e5e7eb";
  };

  const isTaskOverdue = (tarea) => {
    // ✅ CORREGIR DESFASE: Usar fecha actual sin timezone
    const today = new Date();
    const todaySinTZ = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const fechaFin = new Date(tarea.fecha_fin + "T00:00:00");
    return fechaFin < todaySinTZ && tarea.progreso_porcentaje < 100;
  };

  const shouldTaskBeStarted = (tarea) => {
    // ✅ CORREGIR DESFASE: Usar fecha actual sin timezone
    const today = new Date();
    const todaySinTZ = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const fechaInicio = new Date(tarea.fecha_inicio + "T00:00:00");
    return fechaInicio <= todaySinTZ && tarea.progreso_porcentaje === 0;
  };

  const handleMouseEnter = (e, tarea) => {
    const rect = e.target.getBoundingClientRect();

    setTooltip({
      visible: true,
      tarea,
      position: {
        x: Math.min(rect.left + rect.width / 2, window.innerWidth - 380),
        y: rect.top - 10,
      },
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, tarea: null, position: { x: 0, y: 0 } });
  };

  // Calcular posición de HOY
  const getTodayPosition = () => {
    const today = new Date();
    let todayPosition;

    switch (timeView) {
      case "weeks":
        todayPosition =
          Math.floor((today - fechaMinima) / (1000 * 60 * 60 * 24 * 7)) *
          unitWidth;
        break;
      case "months":
        todayPosition =
          Math.floor((today - fechaMinima) / (1000 * 60 * 60 * 24 * 30)) *
          unitWidth;
        break;
      default:
        todayPosition =
          Math.floor((today - fechaMinima) / (1000 * 60 * 60 * 24)) * unitWidth;
    }

    return todayPosition >= 0 && todayPosition <= totalWidth
      ? todayPosition
      : null;
  };

  const todayPosition = getTodayPosition();

  // Altura dinámica basada en cantidad de tareas
  const rowHeight = 64;
  const headerHeight = 52;
  const ganttHeight = Math.min(600, headerHeight + tareas.length * rowHeight);

  return (
    <>
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          overflow: "hidden",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          height: `${ganttHeight}px`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ✅ NUEVO: Container principal con scroll unificado */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            position: "relative",
          }}
        >
          {/* ✅ NUEVO: Todo el contenido en un solo container scrolleable */}
          <div
            style={{
              minWidth: `${340 + totalWidth}px`,
              height: "100%",
            }}
          >
            {/* Header del timeline - FIJO en la parte superior */}
            <div
              style={{
                display: "flex",
                borderBottom: "1px solid #e2e8f0",
                background: "#f8fafc",
                position: "sticky",
                top: 0,
                zIndex: 10,
                height: `${headerHeight}px`,
                flexShrink: 0,
              }}
            >
              {/* Columna de información FIJA */}
              <div
                style={{
                  width: "340px",
                  padding: "16px 20px",
                  borderRight: "1px solid #e2e8f0",
                  fontWeight: "600",
                  color: "#1e293b",
                  background: "white",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexShrink: 0,
                  position: "sticky",
                  left: 0,
                  zIndex: 20,
                }}
              >
                <Calendar size={16} color="#7c3aed" />
                Tareas del Proyecto
              </div>

              {/* Timeline header - SE MUEVE CON EL SCROLL */}
              <div
                style={{
                  flex: 1,
                  background: "#f8fafc",
                  position: "relative",
                  minWidth: `${totalWidth}px`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    height: "52px",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  {/* Indicador de HOY en header */}
                  {todayPosition !== null && (
                    <div
                      style={{
                        position: "absolute",
                        left: `${todayPosition}px`,
                        top: 0,
                        bottom: 0,
                        width: "2px",
                        background: "#ef4444",
                        zIndex: 15,
                        boxShadow: "0 0 8px rgba(239, 68, 68, 0.5)",
                      }}
                    />
                  )}

                  {Array.from({ length: totalUnits }, (_, i) => {
                    const isWeekend =
                      timeView === "days" &&
                      (() => {
                        const fecha = new Date(fechaMinima);
                        fecha.setDate(fechaMinima.getDate() + i);
                        return fecha.getDay() === 0 || fecha.getDay() === 6;
                      })();

                    const isToday =
                      timeView === "days" &&
                      (() => {
                        const fecha = new Date(fechaMinima);
                        fecha.setDate(fechaMinima.getDate() + i);
                        // ✅ CORREGIR DESFASE: Usar fecha actual sin timezone
                        const today = new Date();
                        const todaySinTZ = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                        return fecha.toDateString() === todaySinTZ.toDateString();
                      })();

                    return (
                      <div
                        key={i}
                        style={{
                          minWidth: `${unitWidth}px`,
                          padding: "8px 4px",
                          borderRight: "1px solid #e5e7eb",
                          textAlign: "center",
                          fontSize: "11px",
                          fontWeight: isToday ? "700" : "500",
                          color: isToday
                            ? "#ef4444"
                            : isWeekend
                            ? "#ef4444"
                            : "#64748b",
                          background: isToday
                            ? "#fef2f2"
                            : isWeekend
                            ? "#fef2f2"
                            : "transparent",
                          position: "relative",
                        }}
                      >
                        {formatTimeUnit(i)}
                        {isToday && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: "2px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              width: "4px",
                              height: "4px",
                              borderRadius: "2px",
                              background: "#ef4444",
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Contenido de tareas - SE MUEVE CON EL MISMO SCROLL */}
            <div>
              {tareas.map((tarea, index) => {
                const position = getTaskPosition(tarea);
                const progressColor = getProgressColor(
                  tarea.progreso_porcentaje
                );
                const isOverdue = isTaskOverdue(tarea);
                const shouldStart = shouldTaskBeStarted(tarea);
                const info = infoFinanciera[tarea.id];

                return (
                  <div
                    key={tarea.id}
                    style={{
                      display: "flex",
                      borderBottom:
                        index < tareas.length - 1
                          ? "1px solid #f1f5f9"
                          : "none",
                      height: `${rowHeight}px`,
                      background: isOverdue
                        ? "#fef2f2"
                        : shouldStart
                        ? "#fefbf3"
                        : "transparent",
                      flexShrink: 0,
                    }}
                  >
                    {/* Columna de información FIJA */}
                    <div
                      style={{
                        width: "340px",
                        padding: "12px 20px",
                        borderRight: "1px solid #e2e8f0",
                        background: "white",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        flexShrink: 0,
                        position: "sticky",
                        left: 0,
                        zIndex: 10,
                      }}
                    >
                      <div
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "8px",
                          background: `${progressColor}15`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          position: "relative",
                        }}
                      >
                        {tarea.es_hito ? (
                          <Star
                            size={14}
                            color={progressColor}
                            fill={progressColor}
                          />
                        ) : tarea.progreso_porcentaje >= 100 ? (
                          <CheckCircle2 size={14} color={progressColor} />
                        ) : tarea.progreso_porcentaje > 0 ? (
                          <Play size={14} color={progressColor} />
                        ) : (
                          <Pause size={14} color={progressColor} />
                        )}

                        {/* Indicador de alerta financiera */}
                        {info?.alertaFinanciera && (
                          <div
                            style={{
                              position: "absolute",
                              top: "-2px",
                              right: "-2px",
                              width: "8px",
                              height: "8px",
                              borderRadius: "4px",
                              background: "#f59e0b",
                              animation:
                                "financialAlert 1s ease-in-out infinite alternate",
                            }}
                          />
                        )}

                        {isOverdue && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: "-2px",
                              right: "-2px",
                              width: "8px",
                              height: "8px",
                              borderRadius: "4px",
                              background: "#ef4444",
                            }}
                          />
                        )}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1e293b",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            marginBottom: "4px",
                          }}
                        >
                          {tarea.es_hito && <Crown size={12} color="#f59e0b" />}
                          {tarea.nombre}
                        </div>

                        <div
                          style={{
                            fontSize: "11px",
                            color: "#64748b",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span>
                            {tarea.progreso_porcentaje}% • {position.duration}d
                          </span>
                          {/* Indicador de progreso financiero */}
                          {info && (
                            <span
                              style={{
                                color: info.alertaFinanciera
                                  ? "#f59e0b"
                                  : "#10b981",
                                fontWeight: "600",
                              }}
                            >
                              {info.avanceFinanciero.toFixed(1)}%
                            </span>
                          )}
                          {tarea.etapas_obra?.nombre && (
                            <span>• {tarea.etapas_obra.nombre}</span>
                          )}
                        </div>
                      </div>

                      {/* Botones solo para usuarios con permisos */}
                      {!readOnly && (
                        <div
                          style={{ display: "flex", gap: "6px", flexShrink: 0 }}
                        >
                          {filterVigencia !== 'eliminados' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onTaskClick(tarea);
                              }}
                              style={{
                                width: "28px",
                                height: "28px",
                                borderRadius: "6px",
                                border: "none",
                                background: "#eff6ff",
                                color: "#3b82f6",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition:
                                  "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                touchAction: "manipulation",
                              }}
                              title="Editar tarea"
                            >
                              <Edit size={12} />
                            </button>
                          )}

                          {filterVigencia === 'eliminados' ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onRestoreTask?.(tarea.id);
                              }}
                              style={{
                                width: "28px",
                                height: "28px",
                                borderRadius: "6px",
                                border: "none",
                                background: "#ecfdf5",
                                color: "#10b981",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition:
                                  "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                touchAction: "manipulation",
                              }}
                              title="Restaurar"
                            >
                              <RotateCcw size={12} />
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteTask(tarea.id);
                              }}
                              style={{
                                width: "28px",
                                height: "28px",
                                borderRadius: "6px",
                                border: "none",
                                background: "#fef2f2",
                                color: "#ef4444",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition:
                                  "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                touchAction: "manipulation",
                              }}
                              title="Eliminar tarea"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Área del timeline - SE MUEVE CON EL MISMO SCROLL */}
                    <div
                      style={{
                        flex: 1,
                        position: "relative",
                        height: `${rowHeight}px`,
                        background: "#f8fafc",
                        minWidth: `${totalWidth}px`,
                      }}
                    >
                      {/* Línea de HOY */}
                      {todayPosition !== null && (
                        <div
                          style={{
                            position: "absolute",
                            left: `${todayPosition}px`,
                            top: 0,
                            bottom: 0,
                            width: "2px",
                            background: "#ef4444",
                            zIndex: 10,
                            opacity: 0.8,
                            boxShadow: "0 0 4px rgba(239, 68, 68, 0.5)",
                          }}
                        />
                      )}

                      {/* Barra de la tarea con información financiera */}
                      <div
                        style={{
                          position: "absolute",
                          top: "16px",
                          left: `${position.left}px`,
                          width: `${position.width}px`,
                          height: "32px",
                          background: tarea.es_hito
                            ? "linear-gradient(135deg, #f59e0b, #d97706)"
                            : progressColor,
                          borderRadius: tarea.es_hito ? "6px" : "16px",
                          cursor: readOnly ? "default" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          padding: "0 12px",
                          color: "white",
                          fontSize: "11px",
                          fontWeight: "600",
                          boxShadow: tarea.es_hito
                            ? "0 4px 12px rgba(245, 158, 11, 0.3)"
                            : "0 2px 8px rgba(0,0,0,0.1)",
                          overflow: "hidden",
                          border: tarea.es_hito
                            ? "2px solid #fbbf24"
                            : info?.alertaFinanciera
                            ? "2px solid #f59e0b"
                            : "none",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                        onClick={() =>
                          !readOnly && onTaskClick && onTaskClick(tarea)
                        }
                        onMouseEnter={(e) => {
                          handleMouseEnter(e, tarea);
                          if (!readOnly) {
                            e.target.style.transform = "translateY(-2px)";
                            e.target.style.boxShadow = tarea.es_hito
                              ? "0 8px 20px rgba(245, 158, 11, 0.4)"
                              : "0 6px 16px rgba(0,0,0,0.15)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          handleMouseLeave();
                          if (!readOnly) {
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = tarea.es_hito
                              ? "0 4px 12px rgba(245, 158, 11, 0.3)"
                              : "0 2px 8px rgba(0,0,0,0.1)";
                          }
                        }}
                      >
                        {/* Barra de progreso interna */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            height: "100%",
                            width: `${tarea.progreso_porcentaje}%`,
                            background: "rgba(255,255,255,0.3)",
                            borderRadius: tarea.es_hito ? "4px" : "16px",
                            transition: "width 0.3s ease",
                          }}
                        />

                        {/* Barra de progreso financiero */}
                        {info && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: "2px",
                              left: "2px",
                              height: "4px",
                              width: `${Math.min(info.avanceFinanciero, 100)}%`,
                              background: info.alertaFinanciera
                                ? "#f59e0b"
                                : "#10b981",
                              borderRadius: "2px",
                              transition: "width 0.3s ease",
                            }}
                          />
                        )}

                        <span
                          style={{
                            position: "relative",
                            zIndex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {tarea.es_hito && <Crown size={10} />}
                          {tarea.nombre}
                          {/* Indicador de costo */}
                          {info && (
                            <span
                              style={{
                                marginLeft: "auto",
                                fontSize: "9px",
                                opacity: 0.8,
                              }}
                            >
                              ${(info.costoAsignadoTotal / 1000000).toFixed(1)}M
                            </span>
                          )}
                        </span>

                        {/* Indicador de dependencia */}
                        {tarea.tarea_padre_id && (
                          <ArrowRight
                            size={8}
                            style={{
                              position: "absolute",
                              left: "-12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "rgba(255,255,255,0.8)",
                              zIndex: 2,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip con información financiera */}
      <TaskTooltip
        tarea={tooltip.tarea}
        etapas={etapas}
        subetapas={subetapas}
        position={tooltip.position}
        visible={tooltip.visible}
        infoFinanciera={infoFinanciera}
      />
    </>
  );
};

// ✅ NUEVO MODAL COMPLETO DESDE CERO
const NuevoTaskModal = ({
  isOpen,
  onClose,
  task,
  onSave,
  etapas,
  subetapas,
  tareas = [],
  presupuestosDisponibles = [],
  isEditing = false,
  showCustomAlert,
  isMobile,
  readOnly = false,
  etapaSeleccionada,
  subetapaSeleccionada,
}) => {
  // Prevenir scroll del body cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);
  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    etapa_id: "",
    subetapa_id: "",
    fecha_inicio: "",
    fecha_fin: "",
    progreso_porcentaje: "",
    notas: "",
    tarea_padre_id: "",
    es_hito: false,
    presupuestos_vinculados: [], // Array de IDs de presupuestos
  });

  const [loading, setLoading] = useState(false);
  const [costoCalculado, setCostoCalculado] = useState(null);
  const [ajusteFechas, setAjusteFechas] = useState(null);

  // Resetear formulario cuando abre/cierra
  useEffect(() => {
    if (isOpen) {
      if (task && isEditing) {
        setFormData({
          nombre: task.nombre || "",
          descripcion: task.descripcion || "",
          etapa_id: task.etapa_id || "",
          subetapa_id: task.subetapa_id || "",
          fecha_inicio: task.fecha_inicio || "",
          fecha_fin: task.fecha_fin || "",
          progreso_porcentaje: task.progreso_porcentaje?.toString() || "",
          notas: task.notas || "",
          tarea_padre_id: task.tarea_padre_id || "",
          es_hito: task.es_hito || false,
          presupuestos_vinculados: task.presupuestos_vinculados || [],
        });
      } else {
        // Nueva tarea: usar fechas por defecto y filtros actuales
        const hoy = new Date().toISOString().split("T")[0];
        const mañana = new Date();
        mañana.setDate(mañana.getDate() + 1);

        setFormData({
          nombre: "",
          descripcion: "",
          etapa_id: etapaSeleccionada || "",
          subetapa_id: subetapaSeleccionada || "",
          fecha_inicio: hoy,
          fecha_fin: mañana.toISOString().split("T")[0],
          progreso_porcentaje: "",
          notas: "",
          tarea_padre_id: "",
          es_hito: false,
          presupuestos_vinculados: [],
        });
      }
    }
  }, [isOpen, task, isEditing, etapaSeleccionada, subetapaSeleccionada]);

  // Auto-ajustar fechas basado en dependencia
  useEffect(() => {
    if (formData.tarea_padre_id && !isEditing) {
      const tareaPadre = tareas.find((t) => t.id === formData.tarea_padre_id);
      if (tareaPadre && tareaPadre.fecha_fin) {
        const fechaFinPadre = new Date(tareaPadre.fecha_fin);
        fechaFinPadre.setDate(fechaFinPadre.getDate() + 1);

        const nuevaFechaInicio = fechaFinPadre.toISOString().split("T")[0];
        const nuevaFechaFin = new Date(fechaFinPadre);
        nuevaFechaFin.setDate(nuevaFechaFin.getDate() + 7);

        setFormData((prev) => ({
          ...prev,
          fecha_inicio: nuevaFechaInicio,
          fecha_fin: nuevaFechaFin.toISOString().split("T")[0],
        }));
      }
    }
  }, [formData.tarea_padre_id, tareas, isEditing]);

  // Calcular costo automático basado en presupuestos seleccionados
  useEffect(() => {
    if (
      formData.presupuestos_vinculados.length > 0 &&
      formData.fecha_inicio &&
      formData.fecha_fin
    ) {
      const diasTarea =
        Math.ceil(
          (new Date(formData.fecha_fin) - new Date(formData.fecha_inicio)) /
            (1000 * 60 * 60 * 24)
        ) + 1;

      let costoTotalCalculado = 0;

      formData.presupuestos_vinculados.forEach((presupuestoId) => {
        const presupuesto = presupuestosDisponibles.find(
          (p) => p.id === presupuestoId
        );
        if (!presupuesto) return;

        // Buscar otras tareas que usen este presupuesto
        const otrasTasksConPresupuesto = tareas.filter(
          (t) =>
            t.presupuestos_vinculados &&
            t.presupuestos_vinculados.includes(presupuestoId) &&
            (!isEditing || t.id !== task?.id)
        );

        // Calcular total de días de todas las tareas que usan este presupuesto
        const diasOtrasTareas = otrasTasksConPresupuesto.reduce((sum, t) => {
          const dias =
            Math.ceil(
              (new Date(t.fecha_fin) - new Date(t.fecha_inicio)) /
                (1000 * 60 * 60 * 24)
            ) + 1;
          return sum + dias;
        }, 0);

        const totalDias = diasOtrasTareas + diasTarea;
        const proporcionTarea = totalDias > 0 ? diasTarea / totalDias : 1;

        costoTotalCalculado +=
          presupuesto.total_actualizado_ars * proporcionTarea;
      });

      setCostoCalculado(costoTotalCalculado);
    } else {
      setCostoCalculado(null);
    }
  }, [
    formData.presupuestos_vinculados,
    formData.fecha_inicio,
    formData.fecha_fin,
    presupuestosDisponibles,
    tareas,
    isEditing,
    task,
  ]);

  // Calcular ajuste inteligente de fechas
  useEffect(() => {
    if (
      formData.fecha_inicio &&
      formData.fecha_fin &&
      formData.progreso_porcentaje &&
      isEditing
    ) {
      const progreso = parseInt(formData.progreso_porcentaje);
      if (progreso > 0) {
        const ajuste = calcularAjusteFechas(
          formData.fecha_inicio,
          formData.fecha_fin,
          progreso
        );
        setAjusteFechas(ajuste);
      } else {
        setAjusteFechas(null);
      }
    } else {
      setAjusteFechas(null);
    }
  }, [
    formData.fecha_inicio,
    formData.fecha_fin,
    formData.progreso_porcentaje,
    isEditing,
  ]);

  // Función auxiliar para calcular ajuste de fechas
  const calcularAjusteFechas = (
    fechaInicio,
    fechaFinOriginal,
    progresoActual
  ) => {
    const hoy = new Date();
    const fechaInicioDate = new Date(fechaInicio);
    const fechaFinOriginalDate = new Date(fechaFinOriginal);

    if (fechaInicioDate > hoy) return null; // Tarea no ha empezado
    if (progresoActual === 0) return null; // No se puede calcular sin progreso

    const diasTranscurridos = Math.max(
      1,
      Math.ceil((hoy - fechaInicioDate) / (1000 * 60 * 60 * 24))
    );
    const duracionOriginal = Math.ceil(
      (fechaFinOriginalDate - fechaInicioDate) / (1000 * 60 * 60 * 24)
    );

    // Cálculo: si en X días hicimos Y%, para 100% necesitamos:
    const diasEstimadosTotal = Math.round(
      (diasTranscurridos * 100) / progresoActual
    );
    const nuevaFechaFin = new Date(fechaInicioDate);
    nuevaFechaFin.setDate(nuevaFechaFin.getDate() + diasEstimadosTotal);

    const diferenciaDias = diasEstimadosTotal - duracionOriginal;

    if (Math.abs(diferenciaDias) <= 2) return null; // Diferencia mínima, no sugerir cambio

    return {
      fechaFinOriginal: fechaFinOriginal,
      fechaFinSugerida: nuevaFechaFin.toISOString().split("T")[0],
      diasDiferencia: diferenciaDias,
      tipoAjuste: diferenciaDias > 0 ? "retraso" : "adelanto",
      mensaje:
        diferenciaDias > 0
          ? `Deberías extender ${diferenciaDias} días`
          : `Podrías adelantar ${Math.abs(diferenciaDias)} días`,
    };
  };

  // Aplicar ajuste de fechas
  const aplicarAjusteFechas = () => {
    if (!ajusteFechas) return;

    showCustomAlert(
      "confirm",
      "Ajustar fechas automáticamente",
      `¿Cambiar fecha de finalización a ${new Date(
        ajusteFechas.fechaFinSugerida
      ).toLocaleDateString("es-ES")}?`,
      () => {
        setFormData((prev) => ({
          ...prev,
          fecha_fin: ajusteFechas.fechaFinSugerida,
        }));
        setAjusteFechas(null);
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (readOnly) {
      showCustomAlert(
        "error",
        "Sin permisos",
        "No tienes permisos para modificar tareas"
      );
      return;
    }

    // Validaciones
    if (!formData.nombre.trim()) {
      showCustomAlert("error", "Error", "El nombre de la tarea es obligatorio");
      return;
    }

    if (!formData.fecha_inicio || !formData.fecha_fin) {
      showCustomAlert(
        "error",
        "Error",
        "Las fechas de inicio y fin son obligatorias"
      );
      return;
    }

    if (new Date(formData.fecha_fin) < new Date(formData.fecha_inicio)) {
      showCustomAlert(
        "error",
        "Error",
        "La fecha de fin no puede ser anterior a la fecha de inicio"
      );
      return;
    }

    if (
      formData.tarea_padre_id &&
      isEditing &&
      task.id === formData.tarea_padre_id
    ) {
      showCustomAlert(
        "error",
        "Error",
        "Una tarea no puede depender de sí misma"
      );
      return;
    }

    setLoading(true);
    try {
      const dataToSave = {
        ...formData,
        progreso_porcentaje: formData.progreso_porcentaje
          ? parseInt(formData.progreso_porcentaje)
          : 0,
        costo_estimado: costoCalculado || 0,
        tarea_padre_id: formData.tarea_padre_id || null,
        tiene_dependencias: !!formData.tarea_padre_id,
      };

      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error("Error guardando tarea:", error);
      showCustomAlert("error", "Error", "Error al guardar la tarea");
    }
    setLoading(false);
  };

  // Preparar opciones para dropdowns
  const subetapasFiltradas = subetapas.filter(
    (sub) => sub.etapa_id === formData.etapa_id
  );

  const tareasDisponibles = tareas
    .filter((t) => !isEditing || t.id !== task?.id)
    .sort((a, b) => new Date(a.fecha_inicio) - new Date(b.fecha_inicio));

  const opcionesEtapas = [
    { value: "", label: "Seleccionar etapa" },
    ...etapas.map((etapa) => ({
      value: etapa.id,
      label: etapa.nombre,
    })),
  ];

  const opcionesSubetapas = [
    { value: "", label: "Seleccionar subetapa" },
    ...subetapasFiltradas.map((subetapa) => ({
      value: subetapa.id,
      label: subetapa.nombre,
    })),
  ];

  const opcionesTareasPadre = [
    { value: "", label: "Sin dependencias" },
    ...tareasDisponibles.map((tarea) => ({
      value: tarea.id,
      label: `${tarea.nombre} (${new Date(tarea.fecha_fin).toLocaleDateString(
        "es-ES"
      )})`,
    })),
  ];

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
        overflow: "hidden", // Prevenir scroll del modal container
      }}
      onClick={(e) => {
        // Cerrar modal al hacer click en el backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          // 👇 LÍNEAS AÑADIDAS
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: getVistaColor().gradient,
            color: "white",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Calendar size={20} color="white" />
            </div>
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {readOnly
                ? "Ver Tarea"
                : isEditing
                ? "Editar Tarea"
                : "Nueva Tarea"}
            </h2>
          </div>

          <button
            onClick={onClose}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              border: "none",
              background: "rgba(255,255,255,0.2)",
              color: "white",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Alerta de ajuste inteligente de fechas */}
        {ajusteFechas && (
          <div
            style={{
              padding: "16px 24px",
              background:
                ajusteFechas.tipoAjuste === "retraso" ? "#fef3cd" : "#f0f9ff",
              borderBottom: "1px solid #f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              position: "relative",
              zIndex: 1,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flex: 1,
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background:
                    ajusteFechas.tipoAjuste === "retraso"
                      ? "#f59e0b"
                      : "#3b82f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {ajusteFechas.tipoAjuste === "retraso" ? (
                  <AlertTriangle size={16} color="white" />
                ) : (
                  <Zap size={16} color="white" />
                )}
              </div>
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color:
                      ajusteFechas.tipoAjuste === "retraso"
                        ? "#92400e"
                        : "#1d4ed8",
                    marginBottom: "2px",
                  }}
                >
                  {ajusteFechas.tipoAjuste === "retraso"
                    ? "Progreso más lento"
                    : "Progreso acelerado"}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                  }}
                >
                  {ajusteFechas.mensaje}
                </div>
              </div>
            </div>

            <button
              onClick={aplicarAjusteFechas}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                background:
                  ajusteFechas.tipoAjuste === "retraso" ? "#f59e0b" : "#3b82f6",
                color: "white",
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              <Calendar size={12} />
              Ajustar a{" "}
              {new Date(ajusteFechas.fechaFinSugerida).toLocaleDateString(
                "es-ES"
              )}
            </button>
          </div>
        )}

        {/* Form Content */}
        <div
          style={{
            padding: "24px",
            maxHeight: "calc(90vh - 200px)",
            overflowY: "auto",
            overflowX: "hidden", // Prevenir scroll horizontal
          }}
        >
          <form onSubmit={handleSubmit} id="task-form">
            <div style={{ display: "grid", gap: "20px" }}>
              {/* Nombre */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Nombre de la tarea *
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) =>
                    !readOnly &&
                    setFormData({ ...formData, nombre: e.target.value })
                  }
                  placeholder="Ej: Excavación de cimientos"
                  required
                  disabled={readOnly}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxSizing: "border-box",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                    WebkitAppearance: "none",
                    appearance: "none",
                    touchAction: "manipulation",
                    background: readOnly ? "#f8fafc" : "white",
                    color: readOnly ? "#64748b" : "#1e293b",
                  }}
                  onFocus={(e) => {
                    if (!readOnly) {
                      e.target.style.borderColor = "#7c3aed";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(124, 58, 237, 0.1)";
                    }
                  }}
                  onBlur={(e) => {
                    if (!readOnly) {
                      e.target.style.borderColor = "#f1f5f9";
                      e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                    }
                  }}
                />
              </div>

              {/* Etapa y Subetapa */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "16px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Etapa
                  </label>
                  <CustomDropdown
                    value={formData.etapa_id}
                    onChange={(value) =>
                      !readOnly &&
                      setFormData({
                        ...formData,
                        etapa_id: value,
                        subetapa_id: "",
                      })
                    }
                    options={opcionesEtapas}
                    placeholder="Seleccionar etapa"
                    disabled={readOnly}
                    icon={Building2}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Subetapa
                  </label>
                  <CustomDropdown
                    value={formData.subetapa_id}
                    onChange={(value) =>
                      !readOnly &&
                      setFormData({ ...formData, subetapa_id: value })
                    }
                    options={opcionesSubetapas}
                    placeholder="Seleccionar subetapa"
                    disabled={readOnly || !formData.etapa_id}
                    icon={Building2}
                  />
                </div>
              </div>

              {/* ✅ NUEVO SELECTOR DE PRESUPUESTOS MÚLTIPLES */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Presupuestos vinculados (opcional)
                </label>
                <PresupuestosMultiplesDropdown
                  presupuestosSeleccionados={formData.presupuestos_vinculados}
                  onPresupuestosChange={(nuevosPresupuestos) =>
                    !readOnly &&
                    setFormData({
                      ...formData,
                      presupuestos_vinculados: nuevosPresupuestos,
                    })
                  }
                  presupuestosDisponibles={presupuestosDisponibles}
                  disabled={readOnly}
                  placeholder="Seleccionar presupuestos a vincular"
                />
                <p
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    margin: "4px 0 0 0",
                    fontStyle: "italic",
                  }}
                >
                  Los costos se distribuyen automáticamente según la duración en
                  días y otros presupuestos vinculados.
                </p>
              </div>

              {/* Fechas */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "16px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Fecha de inicio *
                  </label>
                  <input
                    type="date"
                    value={formData.fecha_inicio}
                    onChange={(e) =>
                      !readOnly &&
                      setFormData({ ...formData, fecha_inicio: e.target.value })
                    }
                    required
                    disabled={readOnly}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      border: "2px solid #f1f5f9",
                      borderRadius: "12px",
                      fontSize: "16px",
                      outline: "none",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxSizing: "border-box",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                      WebkitAppearance: "none",
                      appearance: "none",
                      touchAction: "manipulation",
                      background: readOnly ? "#f8fafc" : "white",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Fecha de fin *
                  </label>
                  <input
                    type="date"
                    value={formData.fecha_fin}
                    onChange={(e) =>
                      !readOnly &&
                      setFormData({ ...formData, fecha_fin: e.target.value })
                    }
                    required
                    disabled={readOnly}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      border: "2px solid #f1f5f9",
                      borderRadius: "12px",
                      fontSize: "16px",
                      outline: "none",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxSizing: "border-box",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                      WebkitAppearance: "none",
                      appearance: "none",
                      touchAction: "manipulation",
                      background: readOnly ? "#f8fafc" : "white",
                    }}
                  />
                </div>
              </div>

              {/* Progreso, Costo y Hito */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr auto",
                  gap: "16px",
                  alignItems: "end",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Progreso (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.progreso_porcentaje}
                    onChange={(e) =>
                      !readOnly &&
                      setFormData({
                        ...formData,
                        progreso_porcentaje: e.target.value,
                      })
                    }
                    placeholder="0"
                    disabled={readOnly}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      border: "2px solid #f1f5f9",
                      borderRadius: "12px",
                      fontSize: "16px",
                      outline: "none",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxSizing: "border-box",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                      WebkitAppearance: "none",
                      appearance: "none",
                      touchAction: "manipulation",
                      background: readOnly ? "#f8fafc" : "white",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Costo estimado
                  </label>
                  <input
                    type="text"
                    value={
                      costoCalculado !== null
                        ? `$${costoCalculado.toLocaleString("es-AR")} ARS`
                        : "Sin presupuestos vinculados"
                    }
                    disabled={true}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      border: "2px solid #f1f5f9",
                      borderRadius: "12px",
                      fontSize: "16px",
                      outline: "none",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxSizing: "border-box",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                      WebkitAppearance: "none",
                      appearance: "none",
                      touchAction: "manipulation",
                      background: "#f8fafc",
                      color: costoCalculado !== null ? "#10b981" : "#64748b",
                      fontWeight: costoCalculado !== null ? "600" : "400",
                    }}
                  />
                  {costoCalculado !== null && (
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#10b981",
                        margin: "4px 0 0 0",
                        fontWeight: "500",
                      }}
                    >
                      ✅ Calculado automáticamente
                    </p>
                  )}
                </div>

                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      cursor: readOnly ? "default" : "pointer",
                      padding: "14px 16px",
                      border: "2px solid #f1f5f9",
                      borderRadius: "12px",
                      background: formData.es_hito
                        ? "#fef3cd"
                        : readOnly
                        ? "#f8fafc"
                        : "white",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                      touchAction: "manipulation",
                      minWidth: "120px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.es_hito}
                      onChange={(e) =>
                        !readOnly &&
                        setFormData({ ...formData, es_hito: e.target.checked })
                      }
                      disabled={readOnly}
                      style={{ margin: 0 }}
                    />
                    <Crown
                      size={16}
                      color={formData.es_hito ? "#f59e0b" : "#64748b"}
                    />
                    Es Hito
                  </label>
                </div>
              </div>

              {/* Dependencia */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Depende de (opcional)
                </label>
                <CustomDropdown
                  value={formData.tarea_padre_id}
                  onChange={(value) =>
                    !readOnly &&
                    setFormData({ ...formData, tarea_padre_id: value })
                  }
                  options={opcionesTareasPadre}
                  placeholder="Seleccionar tarea padre"
                  disabled={readOnly}
                  icon={Link}
                />
              </div>

              {/* Descripción */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Descripción
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) =>
                    !readOnly &&
                    setFormData({ ...formData, descripcion: e.target.value })
                  }
                  placeholder="Descripción detallada de la tarea..."
                  rows={3}
                  disabled={readOnly}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    resize: "vertical",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                    WebkitAppearance: "none",
                    appearance: "none",
                    touchAction: "manipulation",
                    background: readOnly ? "#f8fafc" : "white",
                  }}
                />
              </div>

              {/* Notas */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Notas internas
                </label>
                <textarea
                  value={formData.notas}
                  onChange={(e) =>
                    !readOnly &&
                    setFormData({ ...formData, notas: e.target.value })
                  }
                  placeholder="Notas internas sobre la tarea..."
                  rows={2}
                  disabled={readOnly}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    resize: "vertical",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                    WebkitAppearance: "none",
                    appearance: "none",
                    touchAction: "manipulation",
                    background: readOnly ? "#f8fafc" : "white",
                  }}
                />
              </div>
            </div>
          </form>
        </div>

        {/* Botones FIJOS - Fuera del scroll */}
        <div
          style={{
            padding: "24px",
            paddingTop: "0",
            borderTop: "1px solid #f1f5f9",
            background: "white",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "16px",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="button-hover"
              style={{
                flex: 1,
                padding: "14px 24px",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                background: "white",
                color: "#6b7280",
                fontSize: "16px",
                fontWeight: "600",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                opacity: loading ? 0.5 : 1,
                touchAction: "manipulation",
              }}
            >
              {readOnly ? "Cerrar" : "Cancelar"}
            </button>

            {!readOnly && (
              <button
                type="submit"
                form="task-form"
                disabled={loading}
                className="button-hover"
                style={{
                  flex: 2,
                  padding: "14px 24px",
                  border: "none",
                  borderRadius: "12px",
                  background: getVistaColor().gradient,
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  opacity: loading ? 0.7 : 1,
                  touchAction: "manipulation",
                  boxShadow: "0 4px 16px rgba(124, 58, 237, 0.25)",
                }}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="loading-pulse" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {isEditing ? "Actualizar" : "Crear"} Tarea
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ✅ COMPONENTE PRINCIPAL GANTT MEJORADO
function Gantt({ constructora, usuario, permisosCliente }) {
  const [proyectos, setProyectos] = useState([]);
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState(() => {
    // Intentar restaurar el último proyecto seleccionado desde localStorage
    try {
      const saved = localStorage.getItem('gantt_proyectoSeleccionado');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error restaurando proyecto seleccionado:', error);
      return null;
    }
  });
  const [tareas, setTareas] = useState([]);
  const [presupuestosDisponibles, setPresupuestosDisponibles] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [subetapas, setSubetapas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTareas, setLoadingTareas] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showFifoHint, setShowFifoHint] = useState(true);
  const lastScrollYRef = useRef(0);

  const [etapaSeleccionada, setEtapaSeleccionada] = useState("");
  const [subetapaSeleccionada, setSubetapaSeleccionada] = useState("");

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [timeView, setTimeView] = useState("days");
  const [showCurrency, setShowCurrency] = useState("ARS");
  const [filterVigencia, setFilterVigencia] = useState('activos');

  const [datosCliente, setDatosCliente] = useState(null);
  const [verificandoPermisos, setVerificandoPermisos] = useState(true);
  const [infoFinanciera, setInfoFinanciera] = useState({});

  // Función para guardar el proyecto seleccionado en localStorage
  const guardarProyectoSeleccionado = (proyecto) => {
    try {
      localStorage.setItem('gantt_proyectoSeleccionado', JSON.stringify(proyecto));
    } catch (error) {
      console.error('Error guardando proyecto seleccionado:', error);
    }
  };

  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
    options: null,
  });

  const [tipoCambio, setTipoCambio] = useState(1000);

  const colorVista = getVistaColor();
  const esCliente = usuario?.tipo_usuario === "cliente";
  const puedeEditar = !esCliente;

  // Scroll reset obligatorio
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ESC handler obligatorio
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowTaskModal(false);
        setEditingTask(null);
        setCustomModal((prev) => ({ ...prev, isOpen: false }));
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const verificarPermisos = async () => {
      if (!usuario || !constructora) {
        setVerificandoPermisos(false);
        return;
      }

      if (esCliente) {
        const permisos = await obtenerPermisosCliente(usuario, constructora);
        setDatosCliente(permisos);

        if (
          permisos.esCliente &&
          !puedeAccederAVista(permisos.permisos, "gantt")
        ) {
          setVerificandoPermisos(false);
          return;
        }
      }

      setVerificandoPermisos(false);
    };

    verificarPermisos();
  }, [usuario, constructora, esCliente]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (constructora?.id && !verificandoPermisos) {
      cargarDatosIniciales();
    }
  }, [constructora, verificandoPermisos, datosCliente]);

  useEffect(() => {
    if (proyectoSeleccionado) {
      cargarTareas();
      cargarPresupuestosActualizados();
    }
  }, [proyectoSeleccionado]);

  // ✅ Recargar presupuestos cuando cambien los filtros
  useEffect(() => {
    if (proyectoSeleccionado) {
      cargarPresupuestosActualizados();
    }
  }, [etapaSeleccionada, subetapaSeleccionada]);

  useEffect(() => {
    setSubetapaSeleccionada("");
  }, [etapaSeleccionada]);

  useEffect(() => {
    cargarTipoCambio();
  }, []);

  // Calcular información financiera cuando cambian tareas o presupuestos
  useEffect(() => {
    if (
      Array.isArray(tareas) &&
      tareas.length > 0 &&
      Array.isArray(presupuestosDisponibles) &&
      presupuestosDisponibles.length > 0
    ) {
      const infoCalculada = calcularDistribucionAutomatica(
        presupuestosDisponibles,
        tareas
      );
      setInfoFinanciera(infoCalculada);
    } else {
      setInfoFinanciera({});
    }
  }, [tareas, presupuestosDisponibles]);

  // ✅ FORZAR ACTUALIZACIÓN DEL CALENDARIO cuando cambian las tareas
  useEffect(() => {
    if (tareas.length > 0) {
      // Forzar re-render del calendario
      setTareas([...tareas]);
    }
  }, [tareas]);

  const showCustomAlert = (type, title, message, onConfirm = null, options = null) => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
      options,
    });
  };

  const cargarTipoCambio = async () => {
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      const data = await response.json();
      setTipoCambio(data.venta || 1000);
    } catch (error) {
      console.error("Error cargando tipo de cambio:", error);
      setTipoCambio(1000);
    }
  };

  const cargarDatosIniciales = async () => {
    try {
      await Promise.all([cargarProyectos(), cargarEtapas(), cargarSubetapas()]);
    } catch (err) {
      console.error("Error cargando datos iniciales:", err);
    }
    setLoading(false);
  };

  const cargarProyectos = async () => {
    try {
      let data = [];
      let error = null;

      if (filterVigencia === 'eliminados') {
        const { data: idsData, error: idsError } = await supabase
          .from('gantt_tareas')
          .select('proyecto_id')
          .eq('constructora_id', constructora.id)
          .or('activo.eq.false,deleted_at.not.is.null');
        if (idsError) {
          error = idsError;
        } else {
          const ids = Array.from(new Set((idsData || []).map(r => r.proyecto_id).filter(Boolean)));
          if (ids.length > 0) {
            let q = supabase
              .from('proyectos')
              .select('*')
              .eq('constructora_id', constructora.id)
              .in('id', ids)
              .order('codigo');
            if (datosCliente?.esCliente && datosCliente?.proyectoId) {
              if (ids.includes(datosCliente.proyectoId)) {
                q = q.eq('id', datosCliente.proyectoId);
              } else {
                q = null;
                data = [];
              }
            }
            if (q) {
              const res = await q;
              data = res.data || [];
              error = res.error || null;
            }
          } else {
            data = [];
          }
        }
      } else {
        let q = supabase
          .from('proyectos')
          .select('*')
          .eq('constructora_id', constructora.id)
          .eq('activo', true)
          .is('deleted_at', null);
        if (datosCliente?.esCliente && datosCliente?.proyectoId) {
          q = q.eq('id', datosCliente.proyectoId);
        } else {
          q = q.order('obra_finalizada', { ascending: true }).order('codigo');
        }
        const res = await q;
        data = res.data || [];
        error = res.error || null;
      }

      if (error) {
        console.error("Error cargando proyectos:", error);
      } else {
        setProyectos(data || []);

        if (data?.length > 0) {
          if (datosCliente?.esCliente) {
            const proyectoCliente = data.find(
              (p) => p.id === datosCliente.proyectoId
            );
            if (proyectoCliente) {
              setProyectoSeleccionado(proyectoCliente);
              guardarProyectoSeleccionado(proyectoCliente);
            }
          } else {
            // Verificar si el proyecto guardado sigue existiendo
            if (proyectoSeleccionado) {
              const proyectoExiste = data.find(p => p.id === proyectoSeleccionado.id);
              if (!proyectoExiste) {
                // El proyecto guardado ya no existe, establecer uno por defecto
                const primerActivo = data.find((p) => !p.obra_finalizada);
                const proyectoInicial = primerActivo || data[0];
                setProyectoSeleccionado(proyectoInicial);
                guardarProyectoSeleccionado(proyectoInicial);
              }
            } else {
              // No hay proyecto guardado, establecer uno por defecto
              const primerActivo = data.find((p) => !p.obra_finalizada);
              const proyectoInicial = primerActivo || data[0];
              setProyectoSeleccionado(proyectoInicial);
              guardarProyectoSeleccionado(proyectoInicial);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const cargarEtapas = async () => {
    try {
      const { data, error } = await supabase
        .from("etapas_obra")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .is("deleted_at", null)
        .order("orden");

      if (error) {
        console.error("Error cargando etapas:", error);
      } else {
        setEtapas(data || []);
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const cargarSubetapas = async () => {
    try {
      const { data, error } = await supabase
        .from("subetapas_obra")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .is("deleted_at", null)
        .order("orden");

      if (error) {
        console.error("Error cargando subetapas:", error);
      } else {
        setSubetapas(data || []);
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  // ✅ NUEVA FUNCIÓN: Cargar presupuestos actualizados
  const cargarPresupuestosActualizados = async () => {
    if (!proyectoSeleccionado) return;

    try {
      console.log("🔄 Cargando presupuestos actualizados:", {
        proyectoId: proyectoSeleccionado.id,
        etapaSeleccionada,
        subetapaSeleccionada,
      });

      let query = supabase
        .from("presupuestos_iniciales_actualizados")
        .select("*")
        .eq("proyecto_id", proyectoSeleccionado.id)
        .order("tipo", { ascending: true })
        .order("nombre_presupuesto", { ascending: true });

      const { data: todosPresupuestos, error } = await query;

      if (error) {
        console.error("❌ Error cargando presupuestos:", error);
        setPresupuestosDisponibles([]);
        return;
      }

      if (!todosPresupuestos || todosPresupuestos.length === 0) {
        console.warn("⚠️ No se encontraron presupuestos actualizados");
        setPresupuestosDisponibles([]);
        return;
      }

      // Filtrar presupuestos según reglas de negocio
      const presupuestosFiltrados = todosPresupuestos.filter((presupuesto) => {
        // SIEMPRE mostrar honorarios y mano_obra
        if (
          presupuesto.tipo === "honorarios" ||
          presupuesto.tipo === "mano_obra"
        ) {
          return true;
        }

        // Para presupuestos de obra, aplicar filtros (permitiendo mostrar sin filtros)
        if (presupuesto.tipo === "obra") {
          // Si hay filtro de etapa, debe coincidir
          if (etapaSeleccionada && presupuesto.etapa_id !== etapaSeleccionada) {
            console.log(`🔍 Filtrado por etapa: ${presupuesto.nombre_presupuesto} (etapa_id: ${presupuesto.etapa_id} vs seleccionada: ${etapaSeleccionada})`);
            return false;
          }

          // Si hay filtro de subetapa, debe coincidir
          if (
            subetapaSeleccionada &&
            presupuesto.subetapa_id !== subetapaSeleccionada
          ) {
            console.log(`🔍 Filtrado por subetapa: ${presupuesto.nombre_presupuesto} (subetapa_id: ${presupuesto.subetapa_id} vs seleccionada: ${subetapaSeleccionada})`);
            return false;
          }

          return true;
        }

        return false;
      });

      setPresupuestosDisponibles(presupuestosFiltrados);

      console.log("✅ Presupuestos filtrados:", {
        total: todosPresupuestos.length,
        filtrados: presupuestosFiltrados.length,
        filtros_activos: {
          etapa: etapaSeleccionada || "ninguna",
          subetapa: subetapaSeleccionada || "ninguna"
        },
        tipos: presupuestosFiltrados.reduce((acc, p) => {
          acc[p.tipo] = (acc[p.tipo] || 0) + 1;
          return acc;
        }, {}),
      });
    } catch (err) {
      console.error("💥 Error en cargarPresupuestosActualizados:", err);
      setPresupuestosDisponibles([]);
    }
  };

  const cargarTareas = async () => {
    if (!proyectoSeleccionado) return;

    setLoadingTareas(true);
    try {
      let query = supabase
        .from("gantt_tareas")
        .select(
          `
          *,
          etapas_obra(nombre),
          subetapas_obra!left(nombre)
        `
        )
        .eq("proyecto_id", proyectoSeleccionado.id)
        .order("fecha_inicio");

      if (filterVigencia === 'activos') {
        query = query.eq('activo', true).is('deleted_at', null);
      } else if (filterVigencia === 'eliminados') {
        query = query.or('activo.eq.false,deleted_at.not.is.null');
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error cargando tareas:", error);
      } else {
        const tareasNormalizadas = (data || []).map((t) => ({
          ...t,
          presupuestos_vinculados: Array.isArray(t.presupuestos_vinculados)
            ? t.presupuestos_vinculados
            : (typeof t.presupuestos_vinculados === "string"
                ? (() => { try { const arr = JSON.parse(t.presupuestos_vinculados); return Array.isArray(arr) ? arr : []; } catch { return []; } })()
                : []),
        }));
        setTareas(tareasNormalizadas);
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setLoadingTareas(false);
  };

  // ✅ NUEVAS FUNCIONES: Crear y actualizar tareas
  const crearTarea = async (datosNuevaTarea) => {
    try {
      const { data, error } = await supabase
        .from("gantt_tareas")
        .insert([
          {
            ...datosNuevaTarea,
            proyecto_id: proyectoSeleccionado.id,
            constructora_id: constructora.id,
            created_by: usuario.id,
          },
        ])
        .select(
          `
          *,
          etapas_obra(nombre),
          subetapas_obra!left(nombre)
        `
        )
        .single();

      if (error) throw error;

      await cargarTareas();

      // ✅ Actualización automática al crear tarea
      await servicioActualizacionDiaria.ejecutarActualizacionManual();
      await cargarPresupuestosActualizados();

      if (data.tarea_padre_id) {
        await actualizarDependencias(data);
      }
    } catch (err) {
      console.error("Error creando tarea:", err);
      throw err;
    }
  };

  const actualizarTarea = async (tareaId, datosActualizados) => {
    try {
      const tareaAnterior = tareas.find((t) => t.id === tareaId);

      const { data, error } = await supabase
        .from("gantt_tareas")
        .update({
          ...datosActualizados,
          updated_by: usuario.id,
          updated_at: new Date().toISOString(),
        })
        .eq("id", tareaId)
        .select(
          `
          *,
          etapas_obra(nombre),
          subetapas_obra!left(nombre)
        `
        )
        .single();

      if (error) throw error;

      await cargarTareas();

      if (
        datosActualizados.fecha_fin &&
        datosActualizados.fecha_fin !== tareaAnterior?.fecha_fin
      ) {
        await actualizarTareasDependientes(
          tareaId,
          datosActualizados.fecha_fin
        );
      }
    } catch (err) {
      console.error("Error actualizando tarea:", err);
      throw err;
    }
  };

  const eliminarTarea = async (tareaId) => {
    const tarea = tareas.find((t) => t.id === tareaId);

    showCustomAlert(
      "confirm",
      "¿Eliminar tarea?",
      "¿Deseas eliminar solo esta tarea o también todas las encadenadas?",
      null,
      [
        {
          label: "Eliminar solo esta",
          onClick: async () => {
            try {
              const { error } = await supabase.rpc('rpc_soft_delete_gantt_tarea', {
                p_tarea_id: tareaId,
                p_usuario_id: usuario?.id || null,
                p_razon: null,
                p_modo: 'solo'
              });
              if (error) throw error;
              await cargarTareas();
              showCustomAlert("success", "Éxito", "Tarea eliminada correctamente");
            } catch (err) {
              showCustomAlert("error", "Error", err.message || "Ocurrió un error al eliminar la tarea.");
            }
          }
        },
        {
          label: "Eliminar en cascada",
          onClick: async () => {
            try {
              const { error } = await supabase.rpc('rpc_soft_delete_gantt_tarea', {
                p_tarea_id: tareaId,
                p_usuario_id: usuario?.id || null,
                p_razon: null,
                p_modo: 'cascada'
              });
              if (error) throw error;
              await cargarTareas();
              showCustomAlert("success", "Éxito", "Tareas eliminadas en cascada correctamente");
            } catch (err) {
              showCustomAlert("error", "Error", err.message || "Ocurrió un error al eliminar las tareas en cascada.");
            }
          }
        }
      ]
    );
  };

  const restaurarTarea = async (tareaId) => {
    showCustomAlert(
      "confirm",
      "¿Restaurar tarea?",
      "¿Deseas restaurar solo esta tarea o también todas las encadenadas?",
      null,
      [
        {
          label: "Restaurar solo esta",
          onClick: async () => {
            try {
              const { error } = await supabase.rpc('rpc_restore_gantt_tarea', {
                p_tarea_id: tareaId,
                p_usuario_id: usuario?.id || null,
                p_modo: 'solo'
              });
              if (error) throw error;
              await cargarTareas();
              showCustomAlert("success", "Éxito", "Tarea restaurada correctamente.");
            } catch (err) {
              showCustomAlert("error", "Error", err.message || "Ocurrió un error al restaurar la tarea.");
            }
          }
        },
        {
          label: "Restaurar en cascada",
          onClick: async () => {
            try {
              const { error } = await supabase.rpc('rpc_restore_gantt_tarea', {
                p_tarea_id: tareaId,
                p_usuario_id: usuario?.id || null,
                p_modo: 'cascada'
              });
              if (error) throw error;
              await cargarTareas();
              showCustomAlert("success", "Éxito", "Tareas restauradas en cascada correctamente.");
            } catch (err) {
              showCustomAlert("error", "Error", err.message || "Ocurrió un error al restaurar las tareas en cascada.");
            }
          }
        }
      ]
    );
  };

  const actualizarTareasDependientes = async (tareaId, nuevaFechaFin) => {
    try {
      const tareasHijas = tareas.filter((t) => t.tarea_padre_id === tareaId);

      for (const tareaHija of tareasHijas) {
        const fechaFinPadre = new Date(nuevaFechaFin);
        fechaFinPadre.setDate(fechaFinPadre.getDate() + 1);

        const duracionOriginal = Math.ceil(
          (new Date(tareaHija.fecha_fin) - new Date(tareaHija.fecha_inicio)) /
            (1000 * 60 * 60 * 24)
        );
        const nuevaFechaFinHija = new Date(fechaFinPadre);
        nuevaFechaFinHija.setDate(
          nuevaFechaFinHija.getDate() + duracionOriginal
        );

        await supabase
          .from("gantt_tareas")
          .update({
            fecha_inicio: fechaFinPadre.toISOString().split("T")[0],
            fecha_fin: nuevaFechaFinHija.toISOString().split("T")[0],
            updated_by: usuario.id,
            updated_at: new Date().toISOString(),
          })
          .eq("id", tareaHija.id);

        await actualizarTareasDependientes(
          tareaHija.id,
          nuevaFechaFinHija.toISOString().split("T")[0]
        );
      }

      await cargarTareas();
    } catch (err) {
      console.error("Error actualizando dependencias:", err);
    }
  };

  const actualizarDependencias = async (nuevaTarea) => {
    if (nuevaTarea.tarea_padre_id) {
      console.log("Nueva tarea creada con dependencia:", nuevaTarea.nombre);
    }
  };

  const handleTaskClick = (tarea) => {
    setEditingTask(tarea);
    setShowTaskModal(true);
  };

  const handleSaveTask = async (datosForm) => {
    if (editingTask) {
      await actualizarTarea(editingTask.id, datosForm);
    } else {
      await crearTarea(datosForm);
    }
    setEditingTask(null);
  };

  const crearCronogramaInicial = async () => {
    if (!proyectoSeleccionado?.fecha_inicio) {
      showCustomAlert(
        "error",
        "Error",
        "El proyecto debe tener una fecha de inicio configurada"
      );
      return;
    }

    showCustomAlert(
      "confirm",
      "Crear cronograma inicial",
      "¿Crear cronograma inicial basado en las etapas estándar?",
      async () => {
        try {
          setLoadingTareas(true);

          const fechaInicioProyecto = new Date(
            proyectoSeleccionado.fecha_inicio
          );
          let fechaActual = new Date(fechaInicioProyecto);

          const tareasIniciales = [];

          for (const etapa of etapas.slice(0, 8)) {
            const duracionEstimada = 14;
            const fechaFin = new Date(fechaActual);
            fechaFin.setDate(fechaFin.getDate() + duracionEstimada);

            const nuevaTarea = {
              nombre: etapa.nombre,
              descripcion: `Etapa: ${etapa.nombre}`,
              etapa_id: etapa.id,
              fecha_inicio: fechaActual.toISOString().split("T")[0],
              fecha_fin: fechaFin.toISOString().split("T")[0],
              progreso_porcentaje: 0,
              notas: "Tarea generada automáticamente",
              proyecto_id: proyectoSeleccionado.id,
              constructora_id: constructora.id,
              created_by: usuario.id,
              presupuestos_vinculados: [],
            };

            tareasIniciales.push(nuevaTarea);
            fechaActual.setDate(fechaActual.getDate() + 3);
          }

          const { data, error } = await supabase
            .from("gantt_tareas")
            .insert(tareasIniciales).select(`
              *,
              etapas_obra(nombre),
              subetapas_obra!left(nombre)
            `);

          if (error) throw error;

          setTareas(data || []);
          showCustomAlert(
            "success",
            "Éxito",
            "Cronograma inicial creado correctamente"
          );
        } catch (err) {
          console.error("Error creando cronograma inicial:", err);
          showCustomAlert(
            "error",
            "Error",
            "Error al crear el cronograma inicial"
          );
        }
        setLoadingTareas(false);
      }
    );
  };

  // Recargar cuando cambia la vigencia o el proyecto
  useEffect(() => {
    if (!proyectoSeleccionado) return;
    cargarTareas();
  }, [filterVigencia, proyectoSeleccionado]);

  const tareasFiltradas = tareas.filter(
    (tarea) =>
      tarea.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tarea.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tarea.etapas_obra?.nombre
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      tarea.subetapas_obra?.nombre
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const calcularEstadisticasProyecto = () => {
    if (tareasFiltradas.length === 0) return null;

    const completadas = tareasFiltradas.filter(
      (t) => t.progreso_porcentaje >= 100
    ).length;
    const enProgreso = tareasFiltradas.filter(
      (t) => t.progreso_porcentaje > 0 && t.progreso_porcentaje < 100
    ).length;
    const pendientes = tareasFiltradas.filter(
      (t) => t.progreso_porcentaje === 0
    ).length;

    const progresoPonderado = tareasFiltradas.reduce(
      (sum, t) => sum + (parseFloat(t.progreso_porcentaje) || 0),
      0
    );
    const avanceGlobal =
      tareasFiltradas.length > 0
        ? Math.round(progresoPonderado / tareasFiltradas.length)
        : 0;

    const fechasFinales = tareasFiltradas.map((t) => new Date(t.fecha_fin));
    const fechaFinalizacionDate =
      fechasFinales.length > 0 ? new Date(Math.max(...fechasFinales)) : null;
    const fechaFinalizacion = fechaFinalizacionDate
      ? fechaFinalizacionDate.toLocaleDateString("es-ES", {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "No determinada";

    const hoy = new Date();
    const tareasAtrasadas = tareasFiltradas.filter((t) => {
      const fechaFin = new Date(t.fecha_fin);
      return fechaFin < hoy && t.progreso_porcentaje < 100;
    }).length;

    const tareasQueDeberianEmpezar = tareasFiltradas.filter((t) => {
      const fechaInicio = new Date(t.fecha_inicio);
      return fechaInicio <= hoy && t.progreso_porcentaje === 0;
    }).length;

    const diasHastaFinalizacion = fechaFinalizacionDate
      ? Math.ceil((fechaFinalizacionDate - hoy) / (1000 * 60 * 60 * 24))
      : null;

    const alertasFinancieras = Object.values(infoFinanciera).filter(
      (info) => info.alertaFinanciera
    ).length;

    return {
      completadas,
      enProgreso,
      pendientes,
      avanceGlobal,
      fechaFinalizacion,
      fechaFinalizacionDate,
      diasHastaFinalizacion,
      tareasAtrasadas,
      tareasQueDeberianEmpezar,
      alertasFinancieras,
    };
  };

  const estadisticas = calcularEstadisticasProyecto();

  const opcionesProyectos = proyectos.map((proyecto) => ({
    value: proyecto.id,
    label: `${proyecto.codigo} - ${proyecto.apellido_cliente}${
      proyecto.obra_finalizada ? " (Finalizado)" : ""
    }`,
  }));

  const opcionesVistaTemp = [
    { value: "days", label: "Días" },
    { value: "weeks", label: "Semanas" },
    { value: "months", label: "Meses" },
  ];

  const opcionesEtapas = [
    { value: "", label: "Todas las etapas" },
    ...etapas.map((etapa) => ({
      value: etapa.id,
      label: etapa.nombre,
    })),
  ];

  const opcionesSubetapas = [
    { value: "", label: "Todas las subetapas" },
    ...subetapas
      .filter(
        (subetapa) =>
          !etapaSeleccionada || subetapa.etapa_id === etapaSeleccionada
      )
      .map((subetapa) => ({
        value: subetapa.id,
        label: subetapa.nombre,
      })),
  ];

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y > lastScrollYRef.current + 10) {
        // bajando
        setShowFifoHint(false);
      } else if (y < lastScrollYRef.current - 10) {
        // subiendo
        setShowFifoHint(true);
      }
      lastScrollYRef.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (verificandoPermisos) {
    return (
      <div
        style={{
          padding: "40px 20px",
          textAlign: "center",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8fafc",
        }}
      >
        <Loader2 size={32} color={colorVista.solid} className="loading-pulse" />
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#1e293b",
            margin: "16px 0 8px 0",
          }}
        >
          Verificando permisos
        </h3>
        <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
          Preparando acceso al cronograma...
        </p>
      </div>
    );
  }

  if (datosCliente?.esCliente && datosCliente?.error) {
    return (
      <div
        style={{
          padding: isMobile ? "20px" : "40px",
          textAlign: "center",
          background: "#f8fafc",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "40px",
            borderRadius: "20px",
            border: "2px solid #fecaca",
            maxWidth: "400px",
            width: "100%",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "20px" }}>🚫</div>
          <h3 style={{ color: "#dc2626", marginBottom: "10px" }}>
            Acceso Denegado
          </h3>
          <p style={{ color: "#ef4444", marginBottom: "20px" }}>
            {datosCliente.error}
          </p>
          <p style={{ color: "#64748b", fontSize: "14px" }}>
            Contacta a tu constructora si crees que esto es un error.
          </p>
        </div>
      </div>
    );
  }

  if (esCliente && datosCliente?.tipoProyecto === "llave_en_mano") {
    return (
      <div
        style={{
          padding: isMobile ? "30px 20px" : "50px 40px",
          textAlign: "center",
          background: "#f8fafc",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "40px",
            borderRadius: "20px",
            border: "2px solid #fbbf24",
            maxWidth: "500px",
            width: "100%",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ fontSize: "64px", marginBottom: "24px" }}>📅</div>
          <h2
            style={{
              color: "#dc2626",
              marginBottom: "16px",
              fontSize: isMobile ? "20px" : "24px",
            }}
          >
            Cronograma no Disponible
          </h2>
          <p style={{ color: "#64748b", fontSize: "15px", lineHeight: "1.5" }}>
            Los clientes de obras Llave en Mano no tienen acceso al cronograma
            detallado.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          padding: "40px 20px",
          textAlign: "center",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8fafc",
        }}
      >
        <Loader2 size={32} color={colorVista.solid} className="loading-pulse" />
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#1e293b",
            margin: "16px 0 8px 0",
          }}
        >
          Cargando cronograma
        </h3>
        <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
          Preparando la vista del proyecto...
        </p>
      </div>
    );
  }

  if (!proyectoSeleccionado) {
    return (
      <div
        style={{
          padding: isMobile ? "20px" : "40px",
          textAlign: "center",
          background: "#f8fafc",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "40px",
            borderRadius: "20px",
            border: "1px solid #f1f5f9",
            maxWidth: "400px",
            width: "100%",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          }}
        >
          <Calendar
            size={48}
            color={colorVista.solid}
            style={{ marginBottom: "20px" }}
          />
          <h3
            style={{
              color: "#1e293b",
              marginBottom: "12px",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            No hay proyectos disponibles
          </h3>
          <p
            style={{
              color: "#64748b",
              fontSize: "14px",
              lineHeight: "1.5",
              margin: 0,
            }}
          >
            {datosCliente?.esCliente
              ? "No se encontró un proyecto asociado a tu email."
              : "Crea tu primer proyecto para comenzar a gestionar cronogramas."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header completamente fijo */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "#f8fafc",
          paddingBottom: "20px",
          marginBottom: "20px",
        }}
      >
        {/* Header principal */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: "16px",
            marginBottom: "16px",
            minHeight: "56px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: "200px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: colorVista.gradient,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px rgba(124, 58, 237, 0.25)",
                flexShrink: 0,
              }}
            >
              <Calendar size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Cronograma
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Presupuestos actualizados con vinculación múltiple
              </p>
            </div>
          </div>

          {!isMobile && puedeEditar && (
            <button
              onClick={() => {
                setEditingTask(null);
                setShowTaskModal(true);
              }}
              className="button-hover"
              style={{
                background: colorVista.gradient,
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(124, 58, 237, 0.25)",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
                flexShrink: 0,
                minWidth: "max-content",
                whiteSpace: "nowrap",
              }}
            >
              <Plus size={16} />
              Nueva Tarea
            </button>
          )}
        </div>

        {/* Filtros mejorados */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          {(!datosCliente?.esCliente || proyectos.length > 1) && (
            <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: "1 1 520px", minWidth: isMobile ? "100%" : "520px" }}>
              <div style={{ flex: "1 1 380px", minWidth: isMobile ? "100%" : "380px" }}>
                <CustomDropdown
                  value={proyectoSeleccionado?.id || ""}
                  onChange={(proyectoId) => {
                    if (!datosCliente?.esCliente) {
                      const proyecto = proyectos.find((p) => p.id === proyectoId);
                      setProyectoSeleccionado(proyecto);
                      guardarProyectoSeleccionado(proyecto);
                    }
                  }}
                  options={opcionesProyectos}
                  placeholder="Selecciona proyecto"
                  disabled={datosCliente?.esCliente || loadingTareas}
                  icon={Building2}
                />
              </div>
              <div style={{ width: isMobile ? "100%" : "220px" }}>
                <CustomDropdown
                  value={filterVigencia}
                  onChange={setFilterVigencia}
                  options={[
                    { value: 'activos', label: 'Activos' },
                    { value: 'eliminados', label: 'Eliminados' },
                  ]}
                  placeholder="Vigencia"
                  icon={Filter}
                />
              </div>
            </div>
          )}

          <div style={{ position: "relative", flex: "1 1 260px", minWidth: "240px" }}>
            <Search
              size={16}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#64748b",
                zIndex: 1,
              }}
            />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus={false}
              autoComplete="off"
              style={{
                width: "100%",
                padding: "14px 16px 14px 44px",
                border: "2px solid #f1f5f9",
                borderRadius: "12px",
                fontSize: "16px",
                WebkitAppearance: "none",
                appearance: "none",
                background: "white",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                outline: "none",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
            />
          </div>

          <div style={{ width: "160px" }}>
            <CustomDropdown
              value={timeView}
              onChange={setTimeView}
              options={opcionesVistaTemp}
              placeholder="Vista"
              icon={Clock}
            />
          </div>

          {puedeEditar && (
            <div style={{ width: "140px" }}>
              <CustomDropdown
                value={showCurrency}
                onChange={setShowCurrency}
                options={[
                  { value: "ARS", label: "ARS" },
                  { value: "USD", label: "USD" },
                ]}
                placeholder="Moneda"
                icon={DollarSign}
              />
            </div>
          )}

          <div style={{ width: isMobile ? "100%" : "220px" }}>
            <CustomDropdown
              value={etapaSeleccionada}
              onChange={setEtapaSeleccionada}
              options={opcionesEtapas}
              placeholder="Todas las etapas"
              icon={Target}
            />
          </div>

          <div style={{ width: isMobile ? "100%" : "220px" }}>
            <CustomDropdown
              value={subetapaSeleccionada}
              onChange={setSubetapaSeleccionada}
              options={opcionesSubetapas}
              placeholder="Todas las subetapas"
              icon={Activity}
            />
          </div>


        </div>

        {/* ✅ Stats mejoradas con fecha de finalización */}
        {!isMobile && estadisticas && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                background: "white",
                padding: "12px",
                borderRadius: "12px",
                textAlign: "center",
                border: "1px solid #f1f5f9",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: `${colorVista.solid}10`,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 8px",
                }}
              >
                <TrendingUp size={16} color={colorVista.solid} />
              </div>
              <p
                style={{
                  fontSize: "10px",
                  color: "#64748b",
                  margin: "0 0 4px 0",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Avance
              </p>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#1e293b",
                  margin: 0,
                }}
              >
                {estadisticas.avanceGlobal}%
              </p>
            </div>

            {/* ✅ NUEVA TARJETA: Fecha de finalización */}
            <div
              style={{
                background: "white",
                padding: "12px",
                borderRadius: "12px",
                textAlign: "center",
                border: "1px solid #10b981",
                boxShadow: "0 2px 8px rgba(16, 185, 129, 0.1)",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: "#10b98110",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 8px",
                }}
              >
                <CalendarCheck size={16} color="#10b981" />
              </div>
              <p
                style={{
                  fontSize: "10px",
                  color: "#64748b",
                  margin: "0 0 4px 0",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Finaliza
              </p>
              <p
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: "#10b981",
                  margin: 0,
                  lineHeight: "1.2",
                }}
              >
                {estadisticas.fechaFinalizacionDate
                  ? estadisticas.fechaFinalizacionDate.toLocaleDateString(
                      "es-ES",
                      {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      }
                    )
                  : "N/D"}
              </p>
            </div>

            <div
              style={{
                background: "white",
                padding: "12px",
                borderRadius: "12px",
                textAlign: "center",
                border: "1px solid #f1f5f9",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: "#10b98110",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 8px",
                }}
              >
                <CheckCircle2 size={16} color="#10b981" />
              </div>
              <p
                style={{
                  fontSize: "10px",
                  color: "#64748b",
                  margin: "0 0 4px 0",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Completas
              </p>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#1e293b",
                  margin: 0,
                }}
              >
                {estadisticas.completadas}
              </p>
            </div>

            <div
              style={{
                background: "white",
                padding: "12px",
                borderRadius: "12px",
                textAlign: "center",
                border: "1px solid #f1f5f9",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: "#f59e0b10",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 8px",
                }}
              >
                <Play size={16} color="#f59e0b" />
              </div>
              <p
                style={{
                  fontSize: "10px",
                  color: "#64748b",
                  margin: "0 0 4px 0",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                En Curso
              </p>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#1e293b",
                  margin: 0,
                }}
              >
                {estadisticas.enProgreso}
              </p>
            </div>

            <div
              style={{
                background: "white",
                padding: "12px",
                borderRadius: "12px",
                textAlign: "center",
                border:
                  estadisticas.tareasAtrasadas > 0
                    ? "1px solid #ef4444"
                    : "1px solid #f1f5f9",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background:
                    estadisticas.tareasAtrasadas > 0
                      ? "#ef444410"
                      : "#6b728010",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 8px",
                }}
              >
                <AlertTriangle
                  size={16}
                  color={
                    estadisticas.tareasAtrasadas > 0 ? "#ef4444" : "#6b7280"
                  }
                />
              </div>
              <p
                style={{
                  fontSize: "10px",
                  color: "#64748b",
                  margin: "0 0 4px 0",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Atrasadas
              </p>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color:
                    estadisticas.tareasAtrasadas > 0 ? "#ef4444" : "#1e293b",
                  margin: 0,
                }}
              >
                {estadisticas.tareasAtrasadas}
              </p>
            </div>

            <div
              style={{
                background:
                  estadisticas.alertasFinancieras > 0 ? "#fef3cd" : "white",
                padding: "12px",
                borderRadius: "12px",
                textAlign: "center",
                border:
                  estadisticas.alertasFinancieras > 0
                    ? "1px solid #f59e0b"
                    : "1px solid #f1f5f9",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background:
                    estadisticas.alertasFinancieras > 0
                      ? "#f59e0b15"
                      : "#10b98110",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 8px",
                }}
              >
                <DollarSign
                  size={16}
                  color={
                    estadisticas.alertasFinancieras > 0 ? "#f59e0b" : "#10b981"
                  }
                />
              </div>
              <p
                style={{
                  fontSize: "10px",
                  color: "#64748b",
                  margin: "0 0 4px 0",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Alertas $
              </p>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color:
                    estadisticas.alertasFinancieras > 0 ? "#f59e0b" : "#10b981",
                  margin: 0,
                }}
              >
                {estadisticas.alertasFinancieras}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Contenido principal */}
      <div
        style={{
          height: isMobile ? "auto" : "calc(100vh - 380px)",
          animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {loadingTareas ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "80px 20px",
              background: "white",
              borderRadius: "16px",
              border: "1px solid #f1f5f9",
              height: isMobile ? "200px" : "100%",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  border: "2px solid #e2e8f0",
                  borderTop: "2px solid #3b82f6",
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                  animation: "spin 1s linear infinite",
                }}
              />
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Cargando...
              </p>
            </div>
          </div>
        ) : tareasFiltradas.length === 0 ? (
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "40px 20px",
              textAlign: "center",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              height: isMobile ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Calendar
              size={48}
              color="#94a3b8"
              style={{ marginBottom: "20px" }}
            />
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#1e293b",
                margin: "0 0 12px 0",
              }}
            >
              {tareas.length === 0
                ? "No hay tareas creadas"
                : "No se encontraron tareas"}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#64748b",
                margin: "0 0 20px 0",
                lineHeight: "1.5",
              }}
            >
              {tareas.length === 0
                ? puedeEditar
                  ? "Comienza creando el cronograma con presupuestos actualizados y vinculación múltiple."
                  : "Tu constructora aún no ha creado el cronograma."
                : "Prueba con otros términos de búsqueda."}
            </p>

            {puedeEditar && tareas.length === 0 && (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={crearCronogramaInicial}
                  className="button-hover"
                  style={{
                    background: colorVista.gradient,
                    color: "white",
                    border: "none",
                    padding: "12px 20px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 4px 16px rgba(124, 58, 237, 0.25)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                >
                  <BarChart3 size={16} />
                  Crear Cronograma Inicial
                </button>

                <button
                  onClick={() => {
                    setEditingTask(null);
                    setShowTaskModal(true);
                  }}
                  className="button-hover"
                  style={{
                    background: "white",
                    color: colorVista.solid,
                    border: `2px solid ${colorVista.solid}`,
                    padding: "12px 20px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                >
                  <Plus size={16} />
                  Nueva Tarea
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {isMobile ? (
              // Cards móviles mejoradas con información de presupuestos actualizados
              <div
                style={{
                  padding: "4px",
                  animation: "fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                {tareasFiltradas.map((tarea, index) => {
                  const diasDuracion =
                    Math.ceil(
                      (new Date(tarea.fecha_fin + "T00:00:00") -
                        new Date(tarea.fecha_inicio + "T00:00:00")) /
                        (1000 * 60 * 60 * 24)
                    ) + 1;
                  const progressColor =
                    tarea.progreso_porcentaje >= 100
                      ? "#10b981"
                      : tarea.progreso_porcentaje >= 75
                      ? "#f59e0b"
                      : tarea.progreso_porcentaje >= 50
                      ? "#3b82f6"
                      : tarea.progreso_porcentaje > 0
                      ? "#8b5cf6"
                      : "#e5e7eb";

                  const isOverdue = (() => {
                    // ✅ CORREGIR DESFASE: Usar fecha actual sin timezone
                    const today = new Date();
                    const todaySinTZ = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    const fechaFin = new Date(tarea.fecha_fin + "T00:00:00");
                    return fechaFin < todaySinTZ && tarea.progreso_porcentaje < 100;
                  })();

                  const shouldStart = (() => {
                    // ✅ CORREGIR DESFASE: Usar fecha actual sin timezone
                    const today = new Date();
                    const todaySinTZ = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    const fechaInicio = new Date(tarea.fecha_inicio + "T00:00:00");
                    return (
                      fechaInicio <= todaySinTZ && tarea.progreso_porcentaje === 0
                    );
                  })();

                  const info = infoFinanciera[tarea.id];

                  return (
                    <div
                      key={tarea.id}
                      onClick={() => handleTaskClick(tarea)}
                      className="task-mobile-card"
                      style={{
                        background: isOverdue
                          ? "#fef2f2"
                          : shouldStart
                          ? "#fefbf3"
                          : "white",
                        border: `1px solid ${
                          isOverdue
                            ? "#fecaca"
                            : shouldStart
                            ? "#fed7aa"
                            : info?.alertaFinanciera
                            ? "#f59e0b"
                            : "#f1f5f9"
                        }`,
                        borderRadius: "12px",
                        padding: "16px",
                        marginBottom: "8px",
                        cursor: puedeEditar ? "pointer" : "default",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        animation: `slideInRight 0.${
                          5 + (index % 10)
                        }s cubic-bezier(0.4, 0, 0.2, 1)`,
                        boxShadow: info?.alertaFinanciera
                          ? "0 4px 12px rgba(245, 158, 11, 0.2)"
                          : "0 1px 3px rgba(0,0,0,0.05)",
                        touchAction: "manipulation",
                        minHeight: "140px",
                      }}
                    >
                      {/* Header con indicadores mejorados */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "12px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "700",
                              color: "#1e293b",
                              marginBottom: "4px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              flexWrap: "wrap",
                            }}
                          >
                            {tarea.es_hito && (
                              <Crown size={12} color="#f59e0b" />
                            )}
                            {tarea.nombre}
                            {tarea.es_hito && (
                              <span
                                style={{
                                  background: "#fef3cd",
                                  color: "#92400e",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontSize: "9px",
                                  fontWeight: "600",
                                }}
                              >
                                HITO
                              </span>
                            )}
                            {info?.alertaFinanciera && (
                              <span
                                style={{
                                  background: "#f59e0b",
                                  color: "white",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontSize: "9px",
                                  fontWeight: "600",
                                }}
                              >
                                ALERTA $
                              </span>
                            )}
                            {info?.presupuestosVinculados &&
                              info.presupuestosVinculados.length > 1 && (
                                <span
                                  style={{
                                    background: "#8b5cf6",
                                    color: "white",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    fontSize: "9px",
                                    fontWeight: "600",
                                  }}
                                >
                                  MÚLTIPLE
                                </span>
                              )}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#64748b",
                              fontWeight: "500",
                            }}
                          >
                            {tarea.etapas_obra?.nombre || "Sin etapa"}
                            {tarea.subetapas_obra?.nombre &&
                              ` • ${tarea.subetapas_obra.nombre}`}
                          </div>
                        </div>
                        <div
                          style={{
                            textAlign: "right",
                            marginLeft: "12px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "18px",
                              fontWeight: "800",
                              color: progressColor,
                              marginBottom: "2px",
                            }}
                          >
                            {tarea.progreso_porcentaje}%
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#64748b",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: "4px",
                            }}
                          >
                            <Calendar size={10} />
                            {diasDuracion}d
                          </div>
                        </div>
                      </div>

                      {/* Información financiera mejorada en móvil */}
                      {info && (
                        <div
                          style={{
                            background: "#f8fafc",
                            padding: "12px",
                            borderRadius: "8px",
                            marginBottom: "12px",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#1e293b",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <CheckCircle2 size={12} color="#10b981" />
                              Control Financiero
                              {info.presupuestosVinculados &&
                                info.presupuestosVinculados.length > 1 &&
                                " (Múltiple)"}
                            </span>
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                color: info.alertaFinanciera
                                  ? "#f59e0b"
                                  : "#10b981",
                              }}
                            >
                              {info.avanceFinanciero.toFixed(1)}% pagado
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: "11px",
                              color: "#64748b",
                            }}
                          >
                            <span>
                              Asignado: $
                              {(info.costoAsignadoTotal / 1000000).toFixed(1)}M
                            </span>
                            <span>
                              Gastado: $
                              {(info.costoGastadoTotal / 1000000).toFixed(1)}M
                            </span>
                          </div>
                          {info.presupuestosVinculados &&
                            info.presupuestosVinculados.length > 1 && (
                              <div
                                style={{
                                  marginTop: "6px",
                                  fontSize: "10px",
                                  color: "#8b5cf6",
                                  fontWeight: "500",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <Settings size={10} />
                                {info.presupuestosVinculados.length}{" "}
                                presupuestos vinculados
                              </div>
                            )}
                        </div>
                      )}

                      {/* Estados y fechas */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          marginBottom: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            background:
                              progressColor === "#e5e7eb"
                                ? "#f1f5f9"
                                : `${progressColor}15`,
                            color:
                              progressColor === "#e5e7eb"
                                ? "#64748b"
                                : progressColor,
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          {tarea.progreso_porcentaje >= 100
                            ? "Completada"
                            : tarea.progreso_porcentaje > 0
                            ? "En progreso"
                            : "Pendiente"}
                        </span>

                        <span
                          style={{
                            background: "#f1f5f9",
                            color: "#64748b",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Clock size={10} />
                          {diasDuracion} días
                        </span>

                        {isOverdue && (
                          <span
                            style={{
                              background: "#fef2f2",
                              color: "#ef4444",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              fontSize: "11px",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <AlertTriangle size={10} />
                            Atrasada
                          </span>
                        )}
                      </div>

                      {/* Fechas y botones */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: "12px",
                          color: "#64748b",
                          marginBottom: "12px",
                        }}
                      >
                        <span>
                          {new Date(tarea.fecha_inicio).toLocaleDateString(
                            "es-ES"
                          )}
                        </span>
                        <span style={{ color: "#94a3b8" }}>hasta</span>
                        <span>
                          {new Date(tarea.fecha_fin).toLocaleDateString(
                            "es-ES"
                          )}
                        </span>
                      </div>

                      {puedeEditar && (
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            paddingTop: "12px",
                            borderTop: "1px solid #f1f5f9",
                          }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingTask(tarea);
                              setShowTaskModal(true);
                            }}
                            className="action-button"
                            style={{
                              background: "#eff6ff",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 8px",
                              cursor: "pointer",
                              color: "#3b82f6",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "12px",
                              fontWeight: "500",
                              transition:
                                "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              touchAction: "manipulation",
                            }}
                          >
                            <Edit size={12} />
                            Editar
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              eliminarTarea(tarea.id);
                            }}
                            className="action-button"
                            style={{
                              background: "#fef2f2",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 8px",
                              cursor: "pointer",
                              color: "#ef4444",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "12px",
                              fontWeight: "500",
                              transition:
                                "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              touchAction: "manipulation",
                            }}
                          >
                            <Trash2 size={12} />
                            Eliminar
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              // Vista desktop - Gantt con presupuestos actualizados
              <UnifiedScrollGanttChart
                tareas={tareasFiltradas}
                onTaskClick={handleTaskClick}
                readOnly={!puedeEditar}
                timeView={timeView}
                showCurrency={showCurrency}
                tipoCambio={tipoCambio}
                onDeleteTask={eliminarTarea}
                onRestoreTask={restaurarTarea}
                filterVigencia={filterVigencia}
                etapas={etapas}
                subetapas={subetapas}
                infoFinanciera={infoFinanciera}
              />
            )}
          </>
        )}
      </div>

      {/* Botón flotante móvil */}
      {isMobile && puedeEditar && (
        <div
          style={{
            position: "fixed",
            bottom: "100px",
            right: "20px",
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => {
              setEditingTask(null);
              setShowTaskModal(true);
            }}
            className="floating-button"
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "28px",
              background: colorVista.gradient,
              color: "white",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              boxShadow: `0 8px 32px ${colorVista.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            <Plus size={24} />
          </button>
        </div>
      )}

      {/* ✅ NUEVO MODAL COMPLETAMENTE REDISEÑADO */}
      <NuevoTaskModal
        isOpen={showTaskModal}
        onClose={() => {
          setShowTaskModal(false);
          setEditingTask(null);
        }}
        task={editingTask}
        onSave={handleSaveTask}
        etapas={etapas}
        subetapas={subetapas}
        tareas={tareas}
        presupuestosDisponibles={presupuestosDisponibles}
        isEditing={!!editingTask}
        showCustomAlert={showCustomAlert}
        isMobile={isMobile}
        readOnly={esCliente}
        etapaSeleccionada={etapaSeleccionada}
        subetapaSeleccionada={subetapaSeleccionada}
      />

      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
        type={customModal.type}
        title={customModal.title}
        message={customModal.message}
        onConfirm={customModal.onConfirm}
        options={customModal.options}
        isMobile={isMobile}
      />

      {/* Nota explicativa - Flotante con auto-hide al hacer scroll */}
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(59, 130, 246, 0.95)",
          color: "white",
          padding: "8px 16px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: "500",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          zIndex: 999,
          maxWidth: "90%",
          textAlign: "center",
          border: "1px solid rgba(255,255,255,0.2)",
          opacity: showFifoHint ? 1 : 0,
          pointerEvents: showFifoHint ? "auto" : "none",
          transition: "opacity 200ms ease",
        }}
      >
        💡 Distribución FIFO: Tareas anteriores cobran primero, luego por
        progreso físico
      </div>

      {/* CSS completo */}
      <style jsx>{`
        /* Prevenir scroll del body cuando hay modales abiertos */
        body.modal-open {
          overflow: hidden !important;
          position: fixed;
          width: 100%;
        }

        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes financialAlert {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.8;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .floating-button:hover {
          transform: scale(1.05);
          box-shadow: 0 12px 40px rgba(124, 58, 237, 0.4);
        }

        .task-overdue {
          animation: taskAlert 1s ease-in-out infinite alternate;
        }

        @keyframes taskAlert {
          0% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
          }
          100% {
            box-shadow: 0 0 0 4px rgba(239, 68, 68, 0);
          }
        }

        .milestone-glow {
          box-shadow: 0 0 20px rgba(245, 158, 11, 0.5);
          animation: milestoneGlow 2s ease-in-out infinite alternate;
        }

        @keyframes milestoneGlow {
          0% {
            box-shadow: 0 0 20px rgba(245, 158, 11, 0.5);
          }
          100% {
            box-shadow: 0 0 30px rgba(245, 158, 11, 0.8);
          }
        }

        .task-mobile-card:active {
          transform: scale(0.98);
          transition: transform 0.1s ease;
        }

        @media (min-width: 769px) {
          .header-section > div:first-child {
            flex-wrap: nowrap !important;
            min-height: 56px;
          }

          .button-hover {
            flex-shrink: 0 !important;
            white-space: nowrap !important;
            min-width: max-content !important;
          }

          .main-container {
            min-width: 1024px;
          }
        }
      `}</style>
    </div>
  );
}

export default Gantt;
